/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.HTMLElement
import kotlin.js.Date
import ml.categorizeTransactionWithAI
import ml.getSelectableCategories
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.fetch.RequestInit

// Transaction data structure for web
data class Transaction(
    val id: String,
    val merchant: String,
    val amount: Double,
    val date: Date,
    val category: String,
    val account: String,
    val isDebit: Boolean = true,
    val aiConfidence: Int? = null, // AI confidence percentage (0-100) - computed from confidenceLevel
    val categorySource: String = "plaid", // "plaid", "ai", "ai_categorization", "pattern_match", "user_learning"
    val categoryLevel1: String = "", // Primary category (e.g., "Food & Dining")
    val categoryLevel2: String = "", // Secondary category (e.g., "Restaurants")
    val categoryLevel3: String = "",  // Specific category (e.g., "Fast Food")
    val confidenceLevel: String? = null, // Plaid confidence level: "VERY_HIGH", "HIGH", "MEDIUM", "LOW", "UNKNOWN"
    // Split/Distribute transaction fields
    val parentTransactionId: String? = null,
    val childType: String? = null, // "SPLIT" or "DISTRIBUTED"
    val isSplitParent: Boolean = false,
    val isDistributedParent: Boolean = false,
    val splitOrder: Int? = null,
    val distributionStartMonth: String? = null,
    val distributionEndMonth: String? = null,
    val distributionMonthIndex: Int? = null,
    val distributionTotalMonths: Int? = null,
    // Goal assignment fields
    val assignedGoalId: String? = null,
    val assignedGoalName: String? = null,
    val assignedGoalIcon: String? = null,
    /** Every goal this transaction feeds. The three fields above name only the
     *  largest, so without this the other goals would be invisible on the row. */
    val assignedGoals: List<models.AssignedGoalShare> = emptyList(),
    // Category verification fields
    val isVerified: Boolean = false,
    val verifiedAt: String? = null,
    // User notes
    val userNotes: String? = null,
    // Exclude flag
    val isExcluded: Boolean = false,
    // Hidden flag (e.g. pending auth-hold transactions paired with a posted card payment)
    val isHidden: Boolean = false,
    // Subscription status
    val isSubscription: Boolean = false,
    // Context assignment
    val contextIds: List<String> = emptyList()
) {
    // Computed property: exclude from totals if parent of split/distribute or excluded
    val excludeFromTotals: Boolean
        get() = models.TransactionEligibility.excludeFromTotals(
            isSplitParent, isDistributedParent, isExcluded,
        )

    val isSplitChild: Boolean
        get() = childType == "SPLIT"

    val isDistributedChild: Boolean
        get() = childType == "DISTRIBUTED"

    // Computed property: check if assigned to a goal
    val hasGoalAssignment: Boolean
        get() = assignedGoalId != null

    // A credit-card payment (raw Plaid PFC in categoryLevel1/2). When such a row
    // is excluded it was auto-excluded as a tracked pair, not user-excluded — the
    // badge reflects that. Single source of truth: PlaidCategories.
    val isCreditCardPayment: Boolean
        get() = models.PlaidCategories.isCreditCardPayment(categoryLevel1, categoryLevel2)

    // An internal transfer. Some banks label a card payment as a plain transfer,
    // so an excluded transfer is shown as auto-excluded in the badge. Single
    // source of truth: PlaidCategories.isTransfer.
    val isTransfer: Boolean
        get() = models.PlaidCategories.isTransfer(categoryLevel1, categoryLevel2)
}

/**
 * Localized text for a failed rename.
 *
 * Server messages are English-only by convention, so the stable `error` code is mapped to a
 * StringKey here rather than shown raw — a French user must never see English server text.
 * `not_found` and `forbidden` are mapped too: they are unreachable through the UI now that
 * the pencil is hidden for manual accounts, but an account deleted in another tab still
 * reaches them, and an unmapped code falls through to the raw English message.
 */
private fun accountRenameErrorText(error: AccountNameError, entered: String): String =
    when (error.code) {
        "duplicate_name" -> StringKey.ACCOUNT_RENAME_DUPLICATE.tr(entered.trim())
        "name_too_long" -> StringKey.ACCOUNT_RENAME_TOO_LONG.tr(models.ACCOUNT_NAME_MAX_LENGTH.toString())
        "not_found" -> StringKey.ACCOUNT_RENAME_NOT_FOUND.tr()
        "forbidden" -> StringKey.ACCOUNT_RENAME_FORBIDDEN.tr()
        else -> error.serverMessage
    }

class SimpleTransactionListComponent(
    private val accessToken: String? = null,
    private val onTransactionClick: (Transaction) -> Unit = {},
    private val onBackClick: () -> Unit = {},
    private var accountId: String? = null,
    private val onAuthError: () -> Unit = {},
    private val onNavClick: ((String) -> Unit)? = null,
    private val onSettingsClick: (() -> Unit)? = null,
    initialAccounts: List<PlaidAccount> = emptyList(),
    private val onAccountSwitch: ((String) -> Unit)? = null,
    private val onAddAccount: (() -> Unit)? = null,
    private val onLogoClick: (() -> Unit)? = null,
    private val forceRefresh: Boolean = false,
    private val showDemoCard: Boolean = false,
    initialCategoryFilter: String? = null,
    private val onCategoryFilterCleared: (() -> Unit)? = null,
    private val isProTier: () -> Boolean = { false },
    // Show the PRO pill: standard AND trialing (not permanent Pro). Distinct from
    // isProTier (access), so trialing users see the tag but keep the feature.
    private val showProTag: () -> Boolean = { false },
    private val onSubscribeClick: (() -> Unit)? = null,
    private val onProUpgradeClick: (() -> Unit)? = null,
    // Host-owned list so icon lookup for user-made primaries/subs works in every row
    // without each component re-fetching. Passed by value so new instances get fresh data.
    private val customCategories: List<CustomCategoryData> = emptyList(),
    // Triggered by the per-account "Import statement" button on manual accounts.
    // The host (WebApp) navigates to Screen.StatementImport(appendToAccountId = …)
    // and the consolidated StatementImportComponent handles the rest.
    private val onImportStatementClick: ((String) -> Unit)? = null,
    // The host (WebApp) already fetches the full /transactions response once to
    // resolve `accounts` before constructing this component. Passing it in here
    // lets loadTransactions() reuse it instead of hitting the endpoint again for
    // the identical data on first load.
    private val preloadedResponse: TransactionsResponse? = null,
) {
    // Local mutable copy of the host-supplied account list. A successful rename updates the
    // matching entry in place (see the account card's rename handler below) so `render()` picks
    // up the new name on the next `rerenderList()` without waiting for a full data refetch.
    private val accounts: MutableList<PlaidAccount> = initialAccounts.toMutableList()

    /**
     * [accounts] in display order: an institution's accounts kept together.
     *
     * The API returns them in database order, which splits a bank apart — two
     * Wealthsimple accounts with an EQ Bank one between them. The rule lives in
     * shared `models.AccountOrder` so the mobile lists can adopt the identical
     * order rather than each inventing one.
     *
     * The saved-order argument is empty for now: grouping is the default and
     * needs no configuration. When manual reordering lands it passes the user's
     * stored list here and nothing else about this changes.
     */
    private fun orderedAccounts(): List<PlaidAccount> {
        val refs = accounts.map { models.AccountRef(it.account_id, it.institution_name) }
        val byId = accounts.associateBy { it.account_id }
        return models.AccountOrder
            .flatten(models.AccountOrder.arrange(refs, emptyList()))
            .mapNotNull { byId[it] }
    }
    private var categoryFilter: String? = initialCategoryFilter
    // Optional second-level filter. Only meaningful when categoryFilter is set.
    // Holds a Plaid detailed code (e.g. "FOOD_AND_DRINK_RESTAURANTS").
    private var subCategoryFilter: String? = null
    private var transactions = mutableListOf<Transaction>()
    // Consumed (and nulled) the first time loadTransactions() runs, so a later
    // call on the same instance (e.g. the manual "Sync" button's forceRefresh)
    // always hits the network rather than replaying stale preloaded data.
    private var pendingPreloadedResponse: TransactionsResponse? = preloadedResponse
    // The full, unfiltered response from the last load — retained so switching
    // the viewed account can re-filter + re-map in memory with zero network calls.
    private var lastResponse: TransactionsResponse? = null
    // Per-account mapped Transaction lists, memoized by account_id. Switching
    // back to a previously-visited account reuses its list here (rather than
    // re-deriving from `lastResponse`) so any local edits made in place while
    // that account was on screen (verify/exclude/split/goal-assign/etc. all
    // mutate `transactions[idx]` directly) survive the round trip instead of
    // being silently reverted to the last-fetched server state.
    private val transactionsByAccount = mutableMapOf<String, MutableList<Transaction>>()
    private var selectedSidebarContextIds = mutableSetOf<String>() // empty = "All"
    private var accountName = "Loading..."
    private var accountBalance = 0.0
    private val apiService = ApiService()
    private var isRefreshing = false
    private var refreshButtonElement: HTMLElement? = null
    private var lastUpdateTimestamp: Double = 0.0
    private var statusLineElement: HTMLElement? = null
    private var bankName: String = "Plaid"
    private var needsRelink: Boolean = false
    private val selectedTransactionIds = mutableSetOf<String>()
    private var showClearConfirmation = false
    private var userCategoryColors: Map<String, String> = emptyMap()

    // Progressive rendering: initial paint is capped at this many rows so the page
    // becomes interactive fast even with 500+ transactions. An IntersectionObserver
    // on a sentinel below the list grows this batch as the user scrolls.
    // Render up to 250 transactions immediately. Most users don't have more
    // than ~6 months of activity, so this covers them in one shot. Heavier
    // users still get progressive loading via the scroll listener and the
    // tap-to-load sentinel.
    private var visibleTxnLimit = 250
    private val txnLoadBatchSize = 200
    // Auth-hold hiding is per-account. This set holds the account_ids the user has
    // opted to reveal pending holds for (empty = all hidden, the default). The list
    // filter checks whether the currently-viewed account is in this set.
    private val showPendingHoldsAccounts = mutableSetOf<String>()
    // Hidden-hold count PER account, computed from the full response at load time (all
    // accounts, before the per-account display filter). Drives the per-account "Show
    // pending holds (N)" checkbox under each account card. Captured at load so it's
    // independent of the racy live `transactions` list.
    private var hiddenHoldCountByAccount: Map<String, Int> = emptyMap()
    private var loadMoreObserver: dynamic = null
    private var loadMoreScrollHandler: ((org.w3c.dom.events.Event) -> Unit)? = null

    fun render(): HTMLElement {
        val isMobile = ResponsiveUtils.isMobile()
        val t = WebTheme.current
        // Install the halo keyframes so the first-visit ⓘ pulse works on this page.
        GuideOverlay.ensureStylesInstalled()
        // Marketing-capture demo mode (?demo=1): auto-click the page's
        // 'i' tour button after 3 s so the tour starts hands-free.
        autoStartTourIfDemoMode("#txn-guide-button")

        val container = document.create.div {
            id = "txn-list-root"
            style = """
                display: flex;
                flex-direction: column;
                min-height: 100dvh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                background: transparent;
                color: ${t.textPrimary};
            """

            // Mobile Menu Overlay (hidden by default)
            if (isMobile) {
                div {
                    unsafe { raw(MarketingMenu.mobileOverlayHtml()) }
                }
            }

            // Top nav bar (responsive) — shrinks on scroll
            div {
                id = "txn-navbar"
                style = if (isMobile) {
                    """
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 12px 16px;
                        background: ${t.navBackground};
                        border-bottom: 1px solid ${t.border};
                        position: sticky;
                        top: 0;
                        z-index: 100;
                        transition: padding 0.25s ease, border-color 0.25s ease;
                    """
                } else {
                    """
                        display: flex;
                        align-items: center;
                        padding: 16px 32px;
                        background: ${t.navBackground};
                        border-bottom: 1px solid ${t.border};
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        z-index: 100;
                        transition: padding 0.25s ease, border-color 0.25s ease;
                    """
                }

                // Logo - clickable to go Home
                img {
                    id = "txn-logo"
                    src = if (WebTheme.current.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
                    alt = "wiz≡r"
                    style = if (isMobile) {
                        "height: 32px; cursor: pointer; transition: height 0.25s ease;"
                    } else {
                        "height: 56px; cursor: pointer; margin-right: 40px; transition: height 0.25s ease;"
                    }
                    title = StringKey.TOOLTIP_GO_HOME.tr()
                    onClickFunction = {
                        onLogoClick?.invoke()
                    }
                }

                // Nav links (desktop only)
                if (!isMobile) {
                    div {
                        attributes["data-guide"] = "nav-tabs"
                        style = """
                            display: flex;
                            gap: 8px;
                            flex: 1;
                        """
                        listOf(
                            Triple("Activity", StringKey.NAV_TRANSACTIONS.tr(), true),
                            Triple("Dashboard", StringKey.NAV_DASHBOARD.tr(), false),
                            Triple("Goals", StringKey.NAV_GOALS.tr(), false),
                            Triple("Budget", StringKey.NAV_BUDGET.tr(), false)
                        ).forEach { (navId, displayLabel, active) ->
                            span {
                                style = """
                                    padding: 8px 16px;
                                    border-radius: 6px;
                                    font-size: 13px;
                                    font-weight: 500;
                                    cursor: pointer;
                                    transition: all 0.15s;
                                    background: ${if (active) t.navBackground else "transparent"};
                                    color: ${if (active) t.textPrimary else t.navText};
                                """
                                onClickFunction = {
                                    clearSelection()
                                    onNavClick?.invoke(navId)
                                }
                                onMouseOverFunction = { event ->
                                    if (!active) {
                                        (event.currentTarget as HTMLElement).style.color = t.textPrimary
                                    }
                                }
                                onMouseOutFunction = { event ->
                                    if (!active) {
                                        (event.currentTarget as HTMLElement).style.color = t.navText
                                    }
                                }
                                +displayLabel
                            }
                        }
                    }
                }

                // Right side actions
                div {
                    style = """
                        display: flex;
                        align-items: center;
                        gap: ${if (isMobile) "8px" else "12px"};
                    """
                    val isManualAccount = models.AccountDisplayName.isManual(accountId)
                    if (isManualAccount) {
                        // Import Statement button for manual accounts
                        button {
                            style = """
                                padding: 6px 12px;
                                background: transparent;
                                border: 1px solid ${t.accent};
                                border-radius: 5px;
                                color: ${t.accent};
                                cursor: pointer;
                                font-size: 12px;
                                font-weight: 500;
                                transition: all 0.15s;
                            """
                            onClickFunction = {
                                accountId?.let { onImportStatementClick?.invoke(it) }
                            }
                            +StringKey.IMPORT_STATEMENT.tr()
                        }
                    } else {
                        // Sync button for Plaid-linked accounts
                        button {
                            style = if (needsRelink) """
                                background: transparent;
                                border: 1px solid ${t.targetOrange};
                                color: ${t.targetOrange};
                                font-size: 12px;
                                cursor: pointer;
                                padding: 6px 12px;
                                border-radius: 5px;
                                transition: all 0.15s;
                            """ else """
                                background: transparent;
                                border: 1px solid ${t.border};
                                color: ${t.textSecondary};
                                font-size: 12px;
                                cursor: pointer;
                                padding: 6px 12px;
                                border-radius: 5px;
                                transition: all 0.15s;
                            """
                            onClickFunction = { event ->
                                if (needsRelink) {
                                    // Trigger relink flow instead of normal sync
                                    onAddAccount?.invoke()
                                } else {
                                    val button = event.target as? HTMLElement
                                    MainScope().launch {
                                        refreshTransactions(button)
                                    }
                                }
                            }
                            id = "refresh-button"
                            attributes["data-guide"] = "sync-button"
                            +(if (needsRelink) StringKey.ACCOUNT_RELINK_BUTTON.tr() else StringKey.ACTIVITY_SYNC.tr())
                        }
                    }
                    // Contextual tour (ⓘ) — pulses on first visit until the user opens or
                    // dismisses the tour once (stored in localStorage per screen).
                    run {
                        val firstVisit = !GuideOverlay.hasSeen(guide.GuideScreen.TRANSACTIONS)
                        val pulseClass = if (firstVisit) "wiz3r-guide-halo" else ""
                        span {
                            id = "txn-guide-button"
                            classes = setOf(pulseClass).filter { it.isNotEmpty() }.toSet()
                            style = """
                                width: 26px; height: 26px;
                                border-radius: 50%;
                                background: ${t.card};
                                color: ${t.accent};
                                border: 1px solid ${t.border};
                                display: inline-flex; align-items: center; justify-content: center;
                                font-size: 14px; font-weight: 700;
                                cursor: pointer;
                                font-family: serif;
                                user-select: none;
                            """
                            title = i18n.StringKey.TOUR_OPEN_TOOLTIP.tr()
                            onClickFunction = {
                                GuideOverlay(guide.GuideScreen.TRANSACTIONS).start()
                                // Remove the halo once the user has opened the tour.
                                (document.getElementById("txn-guide-button") as? HTMLElement)
                                    ?.classList?.remove("wiz3r-guide-halo")
                            }
                            +"i"
                        }
                    }
                    // Language toggle badge
                    span {
                        style = """
                            padding: 4px ${if (isMobile) "8px" else "10px"};
                            border-radius: 4px;
                            background: ${t.card};
                            color: ${t.accent};
                            font-size: 12px;
                            font-weight: 600;
                            cursor: pointer;
                            border: 1px solid ${t.border};
                            transition: all 0.2s;
                        """
                        title = StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()
                        onClickFunction = {
                            LocaleManager.toggleLocale()
                            window.location.href = window.location.pathname
                        }
                        // raw, not `+`: the label carries an inline <svg> globe
                        // that `+` would escape into visible markup.
                        unsafe { raw(languageToggleLabel()) }
                    }
                    // "How Wiz3r works" button → opens the guide as a modal overlay
                    if (!isMobile) {
                        span {
                            id = "nav-guide"
                            style = """
                                width: 32px; height: 32px;
                                border-radius: 50%;
                                background: ${t.card};
                                display: flex; align-items: center; justify-content: center;
                                font-size: 14px;
                                cursor: pointer;
                                border: 1px solid ${t.border};
                            """
                            title = StringKey.TOOLTIP_HOW_IT_WORKS.tr()
                            onClickFunction = { showGuideModal() }
                            +"❓"
                        }
                    }
                    if (isMobile) {
                        // Hamburger menu for mobile. Same id/imperative-attach
                        // pattern as the other mobile pages (Dashboard, Goals,
                        // Budget). Listener wired up below in the post-render
                        // block, scoped to this container's own overlay.
                        button {
                            id = "txn-hamburger"
                            attributes["type"] = "button"
                            attributes["aria-label"] = "Menu"
                            style = "background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer; padding: 4px;"
                            +"☰"
                        }
                    } else {
                        // Hamburger menu icon for desktop
                        span {
                            id = "nav-settings"
                            style = """
                                width: 32px;
                                height: 32px;
                                border-radius: 50%;
                                background: ${t.card};
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                font-size: 16px;
                                cursor: pointer;
                                border: 1px solid ${t.border};
                            """
                            onMouseOverFunction = { event ->
                                (event.currentTarget as HTMLElement).style.background = t.border
                            }
                            onMouseOutFunction = { event ->
                                (event.currentTarget as HTMLElement).style.background = t.card
                            }
                            +"☰"
                        }
                    }
                }
            }

            // Spacer for fixed navbar
            if (!isMobile) {
                div {
                    id = "txn-navbar-spacer"
                    style = "height: 90px;" // matches initial navbar height (16px padding * 2 + 56px logo + borders)
                }
            }

            if (showDemoCard) {
                // Demo card — centered, no sidebar, nav bar stays visible
                div {
                    style = """
                        flex: 1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 24px;
                    """
                    div {
                        style = """
                            background: ${t.card};
                            border-radius: 16px;
                            padding: 32px 28px;
                            max-width: 400px;
                            width: 100%;
                            text-align: center;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            gap: 16px;
                        """
                        p {
                            style = "color: ${t.textPrimary}; font-size: 20px; font-weight: 700; margin: 0;"
                            +StringKey.DASHBOARD_DEMO_CONNECT_TITLE.tr()
                        }
                        p {
                            style = "color: ${t.textSecondary}; font-size: 14px; margin: 0; line-height: 1.5;"
                            +StringKey.ACTIVITY_DEMO_MESSAGE.tr()
                        }
                        button {
                            id = "demo-connect-btn"
                            style = """
                                background: #00d4aa;
                                color: #000;
                                border: none;
                                border-radius: 12px;
                                padding: 12px 32px;
                                font-size: 15px;
                                font-weight: 600;
                                cursor: pointer;
                                margin-top: 8px;
                                min-width: 200px;
                            """
                            onClickFunction = { onAddAccount?.invoke() }
                            +StringKey.DASHBOARD_DEMO_CONNECT_CTA.tr()
                        }
                    }
                }
            } else {
            // Main content area with sidebar — centered with equal margins
            div {
                style = if (isMobile) {
                    """
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        min-height: 0;
                        overflow: hidden;
                    """
                } else {
                    """
                        flex: 1;
                        display: flex;
                        flex-direction: row;
                        min-height: 0;
                        max-width: 1600px;
                        margin: 0 auto;
                        width: 100%;
                    """
                }

                // Mobile Account Selector (shown only on mobile)
                if (isMobile) {
                    div {
                        style = """
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            padding: 12px 16px;
                            background: ${t.navBackground};
                            border-bottom: 1px solid ${t.border};
                        """
                        // Account dropdown for mobile
                        if (accounts.isEmpty()) {
                            span {
                                style = "color: ${t.textSecondary}; font-size: 14px;"
                                +StringKey.ACTIVITY_NO_ACCOUNTS.tr()
                            }
                        } else {
                            // `accounts` is non-empty in this branch, so there is always one to
                            // fall back to — the old "Select Account" literal could never fire.
                            val selectedAccount = accounts.find { it.account_id == accountId } ?: accounts.first()
                            val selectedName = models.AccountDisplayName.primary(
                                selectedAccount.custom_name,
                                selectedAccount.official_name,
                                selectedAccount.name,
                                selectedAccount.mask,
                            )

                            select {
                                style = """
                                    background: ${t.card};
                                    border: 1px solid ${t.border};
                                    border-radius: 8px;
                                    padding: 10px 12px;
                                    color: ${t.textPrimary};
                                    font-size: 14px;
                                    font-weight: 500;
                                    cursor: pointer;
                                    flex: 1;
                                    min-width: 0;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    margin-right: 12px;
                                """
                                onChangeFunction = { event ->
                                    val select = event.target as? org.w3c.dom.HTMLSelectElement
                                    select?.value?.let { selectedAccountId ->
                                        onAccountSwitch?.invoke(selectedAccountId)
                                    }
                                }
                                orderedAccounts().forEach { account ->
                                    val accName = models.AccountDisplayName.primary(
                                        account.custom_name, account.official_name, account.name, account.mask,
                                    )
                                    val balance = account.balance_current ?: account.balance_available ?: 0.0
                                    option {
                                        value = account.account_id
                                        selected = account.account_id == accountId
                                        +"$accName - $${balance.asDynamic().toFixed(2)}"
                                    }
                                }
                            }
                        }
                        // Add account button
                        button {
                            style = """
                                width: 36px;
                                height: 36px;
                                min-width: 36px;
                                flex-shrink: 0;
                                border-radius: 8px;
                                background: ${t.accent};
                                border: none;
                                color: ${if (t.isDark) "#000000" else "#FFFFFF"};
                                font-size: 20px;
                                font-weight: 600;
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            """
                            onClickFunction = {
                                onAddAccount?.invoke()
                            }
                            +"+"
                        }
                    }

                    // Mobile uses an account dropdown, not cards, so the per-account
                    // "Show pending holds (N)" toggle is tied to the selected account and
                    // shown just below the dropdown when that account has hidden holds.
                    val mobileHoldCount = accountId?.let { hiddenHoldCountByAccount[it] } ?: 0
                    val curAccountId = accountId
                    if (mobileHoldCount > 0 && curAccountId != null) {
                        label {
                            style = "display:flex;align-items:center;gap:6px;padding:8px 16px 0 16px;cursor:pointer;font-size:13px;color:${t.textSecondary};background:${t.navBackground};"
                            input(type = InputType.checkBox) {
                                checked = curAccountId in showPendingHoldsAccounts
                                onChangeFunction = { ev ->
                                    if ((ev.target as org.w3c.dom.HTMLInputElement).checked) {
                                        showPendingHoldsAccounts.add(curAccountId)
                                    } else {
                                        showPendingHoldsAccounts.remove(curAccountId)
                                    }
                                    rerenderList()
                                }
                            }
                            +"${StringKey.TRANSACTIONS_SHOW_PENDING_HOLDS.tr()} ($mobileHoldCount)"
                        }
                    }

                    // Mobile contexts section — populated by the same
                    // renderContextsSidebar() function used on desktop. The
                    // chips row uses flex-wrap so it lays out fine in this
                    // narrower container without further CSS changes.
                    div {
                        id = "txn-contexts-section"
                        style = "padding: 8px 16px 12px 16px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border};"
                    }
                }

                // Left Sidebar - Account List (desktop only) — sticky on scroll
                if (!isMobile) {
                    div {
                        id = "txn-left-sidebar"
                        attributes["data-guide"] = "account-selector"
                        style = """
                            width: 260px;
                            background: transparent;
                            border-right: none;
                            padding: 16px;
                            flex-shrink: 0;
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                            position: sticky;
                            top: 80px;
                            align-self: flex-start;
                            max-height: calc(100dvh - 80px);
                            overflow-y: auto;
                        """

                        // Sidebar header
                        div {
                            style = """
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                margin-bottom: 8px;
                            """
                            span {
                                style = """
                                    font-size: 12px;
                                    font-weight: 500;
                                    color: ${t.textSecondary};
                                    text-transform: uppercase;
                                    letter-spacing: 0.5px;
                                """
                                +StringKey.ACTIVITY_ACCOUNTS.tr()
                            }
                            button {
                                style = """
                                    width: 28px;
                                    height: 28px;
                                    border-radius: 6px;
                                    background: ${t.accent};
                                    border: none;
                                    color: ${if (t.isDark) "#000000" else "#FFFFFF"};
                                    font-size: 18px;
                                    font-weight: 600;
                                    cursor: pointer;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    transition: all 0.15s;
                                """
                                onMouseOverFunction = { event ->
                                    (event.currentTarget as HTMLElement).style.background = t.accent
                                }
                                onMouseOutFunction = { event ->
                                    (event.currentTarget as HTMLElement).style.background = t.accent
                                }
                                onClickFunction = {
                                    onAddAccount?.invoke()
                                }
                                +"+"
                            }
                        }

                        // Account cards
                        if (accounts.isEmpty()) {
                            div {
                                style = """
                                    padding: 20px 12px;
                                    text-align: center;
                                    color: ${t.textSecondary};
                                    font-size: 13px;
                                """
                                +StringKey.ACTIVITY_NO_ACCOUNTS.tr()
                            }
                        } else {
                            orderedAccounts().forEach { account ->
                                val isSelected = account.account_id == accountId
                                val displayName = models.AccountDisplayName.primary(
                                    customName = account.custom_name,
                                    officialName = account.official_name,
                                    name = account.name,
                                    mask = account.mask,
                                )

                                div {
                                    style = """
                                        background: ${if (isSelected) "rgba(0, 217, 255, 0.1)" else t.card};
                                        border: 1px solid ${if (isSelected) t.accent else t.border};
                                        border-radius: 8px;
                                        padding: 12px;
                                        cursor: pointer;
                                        transition: all 0.2s;
                                    """
                                    onMouseOverFunction = { event ->
                                        if (!isSelected) {
                                            (event.currentTarget as HTMLElement).style.apply {
                                                background = t.border
                                                borderColor = t.textSecondary
                                            }
                                        }
                                        (document.getElementById("account-rename-btn-${account.account_id}") as? HTMLElement)
                                            ?.style?.opacity = "1"
                                    }
                                    onMouseOutFunction = { event ->
                                        if (!isSelected) {
                                            (event.currentTarget as HTMLElement).style.apply {
                                                background = t.card
                                                borderColor = t.border
                                            }
                                        }
                                        (document.getElementById("account-rename-btn-${account.account_id}") as? HTMLElement)
                                            ?.style?.opacity = "0.75"
                                    }
                                    onClickFunction = {
                                        if (!isSelected) {
                                            onAccountSwitch?.invoke(account.account_id)
                                        }
                                    }

                                    // Institution name (above account name)
                                    if (!account.institution_name.isNullOrBlank()) {
                                        div {
                                            style = """
                                                font-size: 11px;
                                                font-weight: 600;
                                                color: ${t.textSecondary};
                                                margin-bottom: 2px;
                                                white-space: nowrap;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                                text-transform: uppercase;
                                                letter-spacing: 0.5px;
                                            """
                                            +account.institution_name
                                        }
                                    }

                                    // Account name (mask included by the resolver) + rename pencil
                                    div {
                                        style = """
                                            display: flex;
                                            align-items: center;
                                            gap: 4px;
                                            margin-bottom: 4px;
                                        """
                                        span {
                                            style = """
                                                font-size: 13px;
                                                font-weight: 500;
                                                color: ${t.textPrimary};
                                                white-space: nowrap;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                                flex: 1;
                                                min-width: 0;
                                            """
                                            +displayName
                                        }
                                        // Manual accounts live in `manual_accounts` and have no
                                        // custom_name column, so the rename route — which resolves
                                        // through `accounts` — can only 404 for them. Don't offer it.
                                        if (!models.AccountDisplayName.isManual(account.account_id)) {
                                            span {
                                                id = "account-rename-btn-${account.account_id}"
                                                attributes["title"] = StringKey.ACCOUNT_RENAME.tr()
                                                // Always visible, not hover-only: a hover-gated control does
                                                // not exist at all on a touch screen, and at 12px/grey it read
                                                // as decoration rather than a button even on desktop. Accent
                                                // colour + a faint chip make it look pressable; hover and the
                                                // title attribute still say what it does.
                                                style = "cursor: pointer; opacity: 0.75; transition: opacity 0.15s, background 0.15s; " +
                                                    "flex-shrink: 0; font-size: 15px; line-height: 1; color: ${t.accent}; " +
                                                    "padding: 3px 5px; border-radius: 5px; background: rgba(0, 217, 255, 0.10);"
                                                onClickFunction = { e ->
                                                    e.stopPropagation() // the row itself selects the account
                                                    showAccountRenameDialog(account)
                                                }
                                                +"✎"
                                            }
                                        }
                                    }

                                    // What kind of account this is — "Chequing", "Savings",
                                    // "Credit card". Mobile has always shown it; web did not.
                                    // Shared rule (subtype, falling back to type) so all three
                                    // platforms say the same word.
                                    val accountKind = models.AccountDisplayName.typeLabel(
                                        account.subtype, account.type,
                                    )
                                    if (accountKind.isNotEmpty()) {
                                        div {
                                            style = """
                                                font-size: 11px;
                                                color: ${t.textSecondary};
                                                margin-bottom: 6px;
                                            """
                                            +accountKind
                                        }
                                    }

                                    // Balance
                                    div {
                                        style = """
                                            font-size: 14px;
                                            font-weight: 600;
                                            color: ${if (isSelected) t.accent else t.textSecondary};
                                        """
                                        val balance = account.balance_current ?: account.balance_available ?: 0.0
                                        +"$${balance.asDynamic().toFixed(2)}"
                                    }

                                    // Stale / needs-relink badge — clickable, opens Plaid Link in update mode
                                    val needsRelink = account.needs_relink
                                    val staleDays = account.stale_days ?: 0
                                    val isStale = staleDays > staleThresholdFor(account)
                                    val recentlyRelinked = wasRelinkedWithinHours(account.last_relinked_at, 48)
                                    if ((needsRelink || isStale) && !account.item_id.isNullOrBlank()) {
                                        // Three states, ordered by severity:
                                        // 1. needs_relink → red, "Reconnect needed" (auth broken)
                                        // 2. stale + recently relinked → amber, longer text saying
                                        //    Plaid is stuck between us and the bank, suggest waiting
                                        //    or unlinking. Click opens relink anyway as the "try again".
                                        // 3. stale (no recent relink) → amber, original "No new data X days · Reconnect"
                                        val badge: BadgeStyle =
                                            when {
                                                needsRelink -> BadgeStyle(
                                                    "rgba(255,71,87,0.12)",
                                                    "rgba(255,71,87,0.45)",
                                                    "#ff4757",
                                                    StringKey.ACCOUNT_RECONNECT_NEEDED.tr(),
                                                )
                                                recentlyRelinked -> BadgeStyle(
                                                    "rgba(245,158,11,0.12)",
                                                    "rgba(245,158,11,0.45)",
                                                    "#f59e0b",
                                                    StringKey.ACCOUNT_BANK_NOT_RETURNING_DATA.tr(),
                                                )
                                                else -> BadgeStyle(
                                                    "rgba(245,158,11,0.12)",
                                                    "rgba(245,158,11,0.45)",
                                                    "#f59e0b",
                                                    StringKey.ACCOUNT_NO_NEW_DATA_DAYS.tr(staleDays.toString()),
                                                )
                                            }
                                        div {
                                            style = """
                                                display: inline-flex;
                                                align-items: center;
                                                gap: 4px;
                                                margin-top: 8px;
                                                padding: 4px 8px;
                                                border-radius: 999px;
                                                background: ${badge.bg};
                                                border: 1px solid ${badge.border};
                                                color: ${badge.fg};
                                                font-size: 11px;
                                                font-weight: 600;
                                                cursor: pointer;
                                                max-width: 100%;
                                            """
                                            onClickFunction = { e ->
                                                e.stopPropagation()
                                                startRelinkFlow(account.item_id)
                                            }
                                            // For the "bank-not-returning-data" case the badge
                                            // text already explains the situation. The other two
                                            // states append "Reconnect" so the action is obvious.
                                            if (recentlyRelinked && !needsRelink) {
                                                +"⚠ ${badge.text}"
                                            } else {
                                                +"⚠ ${badge.text} · ${StringKey.ACCOUNT_RECONNECT_ACTION.tr()}"
                                            }
                                        }
                                    }

                                    // Transaction count (selected account only)
                                    if (isSelected && transactions.isNotEmpty()) {
                                        div {
                                            style = """
                                                font-size: 11px;
                                                color: ${t.accent};
                                                margin-top: 4px;
                                            """
                                            +"${transactions.size} ${StringKey.SETTINGS_STATEMENT_TRANSACTIONS_COUNT.tr()}"
                                        }
                                    }

                                    // Per-account "Show pending holds (N)" checkbox — INSIDE
                                    // the card, shown only when THIS account has hidden pending
                                    // auth-holds. stopPropagation so ticking it doesn't also
                                    // switch accounts (the card itself is a click target).
                                    val acctHoldCount = hiddenHoldCountByAccount[account.account_id] ?: 0
                                    if (acctHoldCount > 0) {
                                        label {
                                            style = "display:flex;align-items:center;gap:6px;margin-top:8px;padding-top:8px;border-top:1px solid ${t.border};cursor:pointer;font-size:12px;color:${t.textSecondary};"
                                            onClickFunction = { it.stopPropagation() }
                                            input(type = InputType.checkBox) {
                                                checked = account.account_id in showPendingHoldsAccounts
                                                onChangeFunction = { ev ->
                                                    if ((ev.target as org.w3c.dom.HTMLInputElement).checked) {
                                                        showPendingHoldsAccounts.add(account.account_id)
                                                    } else {
                                                        showPendingHoldsAccounts.remove(account.account_id)
                                                    }
                                                    rerenderList()
                                                }
                                            }
                                            +"${StringKey.TRANSACTIONS_SHOW_PENDING_HOLDS.tr()} ($acctHoldCount)"
                                        }
                                    }
                                }
                            }
                        }

                        // Context section placeholder (populated after render)
                        div {
                            id = "txn-contexts-section"
                        }

                        // Batch action menu container (populated dynamically)
                        div {
                            id = "batch-action-menu"
                        }
                    }
                }

                // Main content - responsive layout
                div {
                    style = if (isMobile) {
                        """
                            flex: 1;
                            padding: 16px;
                            display: flex;
                            flex-direction: column;
                            min-height: 0;
                            overflow: hidden;
                        """
                    } else {
                        """
                            flex: 1;
                            padding: 24px 32px;
                            display: grid;
                            grid-template-columns: 1fr minmax(320px, 380px);
                            grid-template-rows: 1fr;
                            gap: 24px;
                            min-height: 0;
                            overflow: visible;
                        """
                    }

                // Left column - Activity list (full width on mobile)
                div {
                    style = """
                        display: flex;
                        flex-direction: column;
                        gap: ${if (isMobile) "12px" else "16px"};
                        min-height: 0;
                        overflow: hidden;
                        flex: 1;
                    """

                    // Balance header
                    div {
                        style = if (isMobile) "margin-bottom: 4px;" else "margin-bottom: 8px;"
                        h2 {
                            style = if (isMobile) {
                                """
                                    font-size: 32px;
                                    font-weight: 600;
                                    margin: 0;
                                    color: ${t.textPrimary};
                                    letter-spacing: -1px;
                                """
                            } else {
                                """
                                    font-size: 48px;
                                    font-weight: 600;
                                    margin: 0;
                                    color: ${t.textPrimary};
                                    letter-spacing: -2px;
                                """
                            }
                            +"$${accountBalance.asDynamic().toFixed(2)}"
                        }
                        p {
                            style = """
                                font-size: ${if (isMobile) "12px" else "14px"};
                                color: ${t.textSecondary};
                                margin: 4px 0 0 0;
                            """
                            +StringKey.ACTIVITY_AVAILABLE_BALANCE.tr(accountName)
                        }
                        p {
                            style = """
                                font-size: 12px;
                                color: ${t.accent};
                                margin: ${if (isMobile) "4px" else "8px"} 0 0 0;
                                min-height: 16px;
                            """
                            id = "status-line"
                            +(getStatusText() ?: "")
                        }
                        // Context total — shown only when context filter is active
                        div {
                            id = "context-total-line"
                            style = "display: none;"
                        }
                    }
                
                // Transaction List - Dark theme
                div {
                    style = """
                        background: ${t.card};
                        border-radius: 12px;
                        border: 1px solid ${t.border};
                        overflow: hidden;
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        min-height: 0;
                        box-shadow: ${t.cardShadow};
                    """

                    // Section header
                    div {
                        style = """
                            padding: 16px 20px;
                            border-bottom: 1px solid ${t.border};
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                        """
                        span {
                            style = """
                                font-size: 14px;
                                font-weight: 500;
                                color: ${t.textSecondary};
                                text-transform: uppercase;
                                letter-spacing: 0.5px;
                            """
                            +StringKey.ACTIVITY_RECENT_ACTIVITY.tr()
                        }
                    }

                    div {
                        style = """
                            flex: 1;
                        """

                        // Category filter — two cascading selects (primary + optional sub).
                        // Replaces the prior chip-only indicator which only appeared
                        // when the filter was set externally (e.g. dashboard pie-chart
                        // click) and gave the user no way to filter from this page.
                        renderCategoryFilterPicker(this, t)

                        // Apply context filter, then build flat sorted list
                        val contextFiltered = if (selectedSidebarContextIds.isEmpty()) transactions
                            else transactions.filter { txn ->
                                val txnCtx = txn.contextIds.toSet()
                                selectedSidebarContextIds.all { it in txnCtx }
                            }
                        // Pending auth-hold rows are hidden by default (they're also
                        // is_excluded server-side, so totals are unaffected) unless the
                        // user opts in via the "Show pending holds" toggle.
                        // Reveal pending holds only if the viewed account is opted in.
                        val holdFiltered =
                            if (accountId != null && accountId in showPendingHoldsAccounts) contextFiltered
                            else contextFiltered.filter { !it.isHidden }
                        val sortedTransactions = buildFlatSortedList(holdFiltered, categoryFilter, subCategoryFilter)
                        // Anchor the "Customize categories" tour step on the first row where
                        // the txn-category cell actually shows the category (rows with a goal
                        // swap that slot for the goal name).
                        val firstNonGoalIdx = sortedTransactions.indexOfFirst { !it.hasGoalAssignment }
                        // Precomputed once per render instead of re-scanning the full
                        // transaction list inside the per-row loop below (was an O(n^2)
                        // scan: ~250 rendered rows x full-list scan each).
                        val childrenByParent = transactions.groupBy { it.parentTransactionId }

                        sortedTransactions.take(visibleTxnLimit).forEachIndexed { txnIdx, transaction ->
                            // Compute children first — needed for allChildrenVerified on the parent badge
                            val isParentTx = transaction.isSplitParent || transaction.isDistributedParent
                            val childTransactions = childrenByParent[transaction.id].orEmpty()
                            // Parent shows "User: 100%" only when every child is independently verified
                            val allChildrenVerified = isParentTx && childTransactions.isNotEmpty() && childTransactions.all { it.isVerified }

                            val isFirstRow = txnIdx == 0
                            div {
                                attributes["data-txn-id"] = transaction.id
                                attributes["data-merchant"] = transaction.merchant
                                attributes["data-category"] = transaction.category
                                // For the category-filter live re-render.
                                attributes["data-cat-primary"] = transaction.categoryLevel1
                                attributes["data-cat-detailed"] = transaction.categoryLevel2
                                if (isFirstRow) attributes["data-guide"] = "txn-row"
                                style = """
                                    display: flex;
                                    align-items: flex-start;
                                    padding: 6px 12px;
                                    transition: background-color 0.15s ease;
                                    cursor: pointer;
                                    border-bottom: 1px solid ${t.border};
                                """
                                onMouseOverFunction = { event ->
                                    (event.currentTarget as HTMLElement).style.backgroundColor = t.border
                                }
                                onMouseOutFunction = { event ->
                                    (event.currentTarget as HTMLElement).style.backgroundColor = "transparent"
                                }
                                onClickFunction = {
                                    if (selectedTransactionIds.isNotEmpty()) {
                                        // In multi-select mode, toggle selection instead of opening detail
                                        val txId = transaction.id
                                        if (selectedTransactionIds.contains(txId)) {
                                            selectedTransactionIds.remove(txId)
                                        } else {
                                            selectedTransactionIds.add(txId)
                                        }
                                        val cColor = getCategoryColor(transaction.categoryLevel1.ifEmpty { transaction.category })
                                        val iconEl = document.getElementById("txn-icon-$txId") as? HTMLElement
                                        if (iconEl != null) {
                                            iconEl.style.backgroundColor = if (selectedTransactionIds.contains(txId)) "${cColor}66" else theme.getIconCircleFill(cColor, t.isDark)
                                        }
                                        renderBatchActionMenu()
                                    } else {
                                        onTransactionClick(transaction)
                                    }
                                }

                                // Left column: Icon + AI Confidence badge
                                div {
                                    style = """
                                        display: flex;
                                        flex-direction: column;
                                        align-items: center;
                                        width: 50px;
                                        margin-right: 12px;
                                        flex-shrink: 0;
                                        position: relative;
                                    """
                                    // Context markers: a small coloured dot per assigned
                                    // context, top-left of the icon — so the row shows its
                                    // context(s) at a glance without opening the detail view.
                                    // pointer-events:none so the dots never block the icon's
                                    // multi-select click underneath.
                                    // Order the assigned contexts by the sidebar's canonical
                                    // order (NOT assignment order) so the dots are consistent
                                    // across every row; take the first 3 when a txn has more.
                                    val assignedCtxIds = transaction.contextIds.toSet()
                                    val rowContexts = sidebarContexts.filter { it.id in assignedCtxIds }
                                    if (rowContexts.isNotEmpty()) {
                                        div {
                                            // Vertically centred on the 36px icon (centre at 18px),
                                            // in the gap to its left. Same 7px, ring-free, raw-colour
                                            // dot as the sidebar context chips. A vertical stack keeps
                                            // multiple contexts centred (1 on the line, 2 straddling it).
                                            style = """
                                                position: absolute; left: 0; top: 18px; transform: translateY(-50%);
                                                display: flex; flex-direction: column; gap: 3px;
                                                pointer-events: none;
                                            """
                                            rowContexts.take(3).forEach { ctx ->
                                                div {
                                                    style = "width: 7px; height: 7px; border-radius: 50%; background: ${ctx.color}; flex-shrink: 0;"
                                                }
                                            }
                                        }
                                    }
                                    // Category Icon (click to multi-select).
                                    // Look up colour by the Plaid code (categoryLevel1, always English)
                                    // — the localized display string never matches the English keyword
                                    // rules in the shared color map and would otherwise fall to grey.
                                    val catLookup = transaction.categoryLevel1.ifEmpty { transaction.category }
                                    val catColor = getCategoryColor(catLookup)
                                    val isSelected = selectedTransactionIds.contains(transaction.id)
                                    div {
                                        id = "txn-icon-${transaction.id}"
                                        attributes["data-cat-color"] = catColor
                                        attributes["data-cat-color-key"] = transaction.categoryLevel1.ifEmpty { transaction.category }
                                        if (isFirstRow) attributes["data-guide"] = "txn-icon"
                                        style = """
                                            width: 36px;
                                            height: 36px;
                                            border-radius: 50%;
                                            background-color: ${if (isSelected) "${t.accent}B3" else theme.getIconCircleFill(catColor, t.isDark)};
                                            border: 2px solid ${if (isSelected) t.accent else catColor};
                                            ${if (isSelected) "box-shadow: 0 0 12px rgba(0, 217, 255, 0.6);" else ""}
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            cursor: pointer;
                                        """
                                        onClickFunction = iconClickHandler@{ event ->
                                            event.stopPropagation()
                                            val txId = transaction.id
                                            if (selectedTransactionIds.contains(txId)) {
                                                selectedTransactionIds.remove(txId)
                                            } else {
                                                // Second selection (already have 1+) requires Pro tier
                                                if (selectedTransactionIds.isNotEmpty() && !isProTier()) {
                                                    val body = document.body
                                                    if (body != null) showProUpgradeModal(body) {
                                                        onProUpgradeClick?.invoke()
                                                    }
                                                    return@iconClickHandler
                                                }
                                                selectedTransactionIds.add(txId)
                                            }
                                            // Update icon style
                                            val iconEl = document.getElementById("txn-icon-$txId") as? HTMLElement
                                            if (iconEl != null) {
                                                val sel = selectedTransactionIds.contains(txId)
                                                iconEl.style.backgroundColor = if (sel) "${t.accent}B3" else theme.getIconCircleFill(catColor, t.isDark)
                                                iconEl.style.borderColor = if (sel) t.accent else catColor
                                                iconEl.style.boxShadow = if (sel) "0 0 12px rgba(0, 217, 255, 0.6)" else "none"
                                            }
                                            // Update action menu in sidebar
                                            renderBatchActionMenu()
                                        }
                                        // Prefer the user's picked icon when the transaction's
                                        // primary/sub matches a custom row. Falls back to the
                                        // built-in Plaid code lookup otherwise.
                                        val userIconSvg = findCustomIconId(
                                            customCategories,
                                            transaction.categoryLevel1,
                                            transaction.categoryLevel2.ifEmpty { null },
                                        )?.let { utils.CustomCategoryIcons.renderSvg(it, size = 18) }
                                        if (userIconSvg != null) {
                                            unsafe { +userIconSvg }
                                        } else {
                                            unsafe { +CategoryIconSvg.renderForCategory(catLookup, transaction.merchant, size = 18) }
                                        }
                                    }

                                    // Confidence badge under icon with verification checkbox/checkmark (using shared CategoryUtils)
                                    val confidenceInfo = utils.CategoryUtils.getConfidenceInfo(
                                        categorySource = transaction.categorySource,
                                        confidenceLevel = transaction.confidenceLevel,
                                        isVerified = transaction.isVerified
                                    )
                                    val badgeColor = when (confidenceInfo.colorCode) {
                                        "dark_green" -> "#2E7D32"
                                        "pale_green" -> "#66BB6A"
                                        "dark_orange" -> "#EF6C00"
                                        "pale_red" -> "#E57373"
                                        "dark_red" -> "#C62828"
                                        "blue" -> "#1976D2"
                                        else -> "#757575"
                                    }
                                    // Badge: parents show checkmark only when all children verified;
                                    // parents never have a verify checkbox (verification is child-driven)
                                    val effectiveShowCheckmark = if (isParentTx) allChildrenVerified else confidenceInfo.showCheckmark
                                    val effectiveLabel = when {
                                        isParentTx && allChildrenVerified -> "AI"
                                        isParentTx -> "AI: 80%"
                                        else -> confidenceInfo.label
                                    }
                                    val effectiveBadgeColor = when {
                                        isParentTx && allChildrenVerified -> "#2E7D32"
                                        isParentTx -> "#66BB6A"
                                        else -> badgeColor
                                    }
                                    div {
                                        id = "verify-badge-${transaction.id}"
                                        if (isFirstRow) attributes["data-guide"] = "ai-badge"
                                        style = """
                                            margin-top: 2px;
                                            display: flex;
                                            align-items: center;
                                            gap: 3px;
                                        """
                                        if (effectiveShowCheckmark) {
                                            span {
                                                style = "font-size: 12px; color: #4CAF50; font-weight: bold;"
                                                +"\u2713"
                                            }
                                        } else if (!isParentTx) {
                                            // Styled box: shows ✓ preview on hover, applies immediately on click
                                            span {
                                                style = """
                                                    display: inline-flex;
                                                    align-items: center;
                                                    justify-content: center;
                                                    width: 16px;
                                                    height: 16px;
                                                    border: 1.5px solid rgba(255,255,255,0.25);
                                                    border-radius: 3px;
                                                    color: transparent;
                                                    font-size: 11px;
                                                    font-weight: bold;
                                                    cursor: pointer;
                                                    transition: border-color 0.15s, color 0.15s;
                                                    flex-shrink: 0;
                                                """
                                                title = StringKey.ACTIVITY_VERIFY_CATEGORY.tr()
                                                onMouseOverFunction = { event ->
                                                    val el = event.currentTarget as org.w3c.dom.HTMLElement
                                                    el.style.borderColor = "#4CAF50"
                                                    el.style.color = "#4CAF50"
                                                    el.textContent = "\u2713"
                                                }
                                                onMouseOutFunction = { event ->
                                                    val el = event.currentTarget as org.w3c.dom.HTMLElement
                                                    el.style.borderColor = "rgba(255,255,255,0.25)"
                                                    el.style.color = "transparent"
                                                    el.textContent = "\u2713"
                                                }
                                                onClickFunction = { event ->
                                                    event.stopPropagation()
                                                    // Immediate DOM update — no waiting for API
                                                    val badgeEl = kotlinx.browser.document.getElementById("verify-badge-${transaction.id}")
                                                    if (badgeEl != null) {
                                                        val verifiedInfo = utils.CategoryUtils.getConfidenceInfo(
                                                            categorySource = transaction.categorySource,
                                                            confidenceLevel = transaction.confidenceLevel,
                                                            isVerified = true
                                                        )
                                                        val verifiedColor = when (verifiedInfo.colorCode) {
                                                            "dark_green" -> "#2E7D32"
                                                            "pale_green" -> "#66BB6A"
                                                            "dark_orange" -> "#EF6C00"
                                                            "pale_red" -> "#E57373"
                                                            "dark_red" -> "#C62828"
                                                            "blue" -> "#1976D2"
                                                            else -> "#757575"
                                                        }
                                                        val labelHtml = if (verifiedInfo.label.isNotEmpty()) {
                                                            "<span style=\"font-size:10px;font-weight:500;color:$verifiedColor;background:${verifiedColor}22;padding:2px 4px;border-radius:4px;white-space:nowrap;\">${verifiedInfo.label}</span>"
                                                        } else ""
                                                        badgeEl.innerHTML = "<span style=\"font-size:12px;color:#4CAF50;font-weight:bold;\">\u2713</span>$labelHtml"
                                                    }
                                                    verifyTransactionCategory(transaction.id)
                                                }
                                                +"\u2713"
                                            }
                                        }
                                        // Confidence label — hidden when empty (user-confirmed)
                                        if (effectiveLabel.isNotEmpty()) {
                                            span {
                                                // Text only — no pill/box behind it (looked amateurish).
                                                style = """
                                                    font-size: 10px;
                                                    font-weight: 500;
                                                    color: $effectiveBadgeColor;
                                                    white-space: nowrap;
                                                """
                                                +effectiveLabel
                                            }
                                        }
                                    }
                                }

                                // Transaction Details - 3 Lines
                                div {
                                    style = """
                                        flex: 1;
                                        min-width: 0;
                                        display: flex;
                                        flex-direction: column;
                                        gap: 1px;
                                    """

                                    // Line 1: Merchant name + split indicator + badges
                                    div {
                                        id = "txn-badges-${transaction.id}"
                                        style = """
                                            display: flex;
                                            align-items: center;
                                            gap: 6px;
                                        """
                                        span {
                                            style = """
                                                font-size: 15px;
                                                font-weight: 500;
                                                color: ${t.textPrimary};
                                                white-space: nowrap;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                            """
                                            +transaction.merchant
                                        }
                                        // Subscription repeat icon
                                        if (transaction.isSubscription) {
                                            span {
                                                style = "display:inline-flex; align-items:center; color:#4CAF50; flex-shrink:0;"
                                                title = "Subscription"
                                                unsafe { +"""<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3" /><path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3" /></svg>""" }
                                            }
                                        }
                                        // Split child pill (orange with split icon + "Split")
                                        if (transaction.isSplitChild) {
                                            span {
                                                style = """
                                                    display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px;
                                                    background: rgba(255, 152, 0, 0.15); border-radius: 4px;
                                                    font-size: 10px; font-weight: 500; color: #FF9800; white-space: nowrap;
                                                """
                                                unsafe {
                                                    +"""<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M21 17h-8l-3.5 -5h-6.5"/><path d="M21 7h-8l-3.495 5"/><path d="M18 10l3 -3l-3 -3"/><path d="M18 20l3 -3l-3 -3"/></svg>"""
                                                }
                                                +StringKey.TRANSACTIONS_SPLIT.tr()
                                            }
                                        }
                                        // Distributed child pill (orange with calendar icon)
                                        if (transaction.isDistributedChild) {
                                            val isMoved = transaction.distributionTotalMonths == 1
                                            span {
                                                style = """
                                                    display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px;
                                                    background: rgba(255, 152, 0, 0.15); border-radius: 4px;
                                                    font-size: 10px; font-weight: 500; color: #FF9800; white-space: nowrap;
                                                """
                                                unsafe {
                                                    +"""<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12"/><path d="M16 3v4"/><path d="M8 3v4"/><path d="M4 11h16"/><path d="M11 15h1"/><path d="M12 15v3"/></svg>"""
                                                }
                                                if (isMoved) {
                                                    +StringKey.TRANSACTION_MOVED_BADGE.tr()
                                                } else {
                                                    val distrLabel = StringKey.TRANSACTIONS_DIST.tr()
                                                    val indexLabel = if (transaction.distributionMonthIndex != null && transaction.distributionTotalMonths != null) {
                                                        " (${transaction.distributionMonthIndex}/${transaction.distributionTotalMonths})"
                                                    } else ""
                                                    +"$distrLabel$indexLabel"
                                                }
                                            }
                                        }
                                        // Excluded label (orange pill with X icon)
                                        if (transaction.isExcluded) {
                                            span {
                                                style = """
                                                    display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px;
                                                    background: rgba(255, 152, 0, 0.15); border-radius: 4px;
                                                    font-size: 10px; font-weight: 500; color: #FF9800; white-space: nowrap;
                                                """
                                                unsafe {
                                                    +"""<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>"""
                                                }
                                                // A tracked card payment is auto-excluded, not
                                                // user-excluded — label it so, same styling. Covers
                                                // both Plaid labellings (explicit card payment, or a
                                                // transfer a bank uses in its place).
                                                if (transaction.isCreditCardPayment || transaction.isTransfer) {
                                                    +StringKey.TRANSACTION_AUTO_EXCLUDED_SHORT.tr()
                                                } else {
                                                    +StringKey.TRANSACTION_EXCLUDED_SHORT.tr()
                                                }
                                            }
                                        }
                                    }

                                    // Line 2: Date + Amount (right-justified)
                                    div {
                                        style = """
                                            display: flex;
                                            align-items: center;
                                            justify-content: space-between;
                                        """
                                        span {
                                            style = """
                                                font-size: 13px;
                                                color: ${t.textSecondary};
                                            """
                                            val dateStr = transaction.date.toLocaleDateString("en-US",
                                                js("{ month: 'short', day: 'numeric', year: 'numeric' }"))
                                            +dateStr
                                        }
                                        val isMoved = transaction.isDistributedChild && transaction.distributionTotalMonths == 1
                                        if ((transaction.isSplitChild || transaction.isDistributedChild) && !isMoved) {
                                            // Fractional amount: child / parent (no icon — icon is in the pill label)
                                            span {
                                                style = "font-size: 14px; font-weight: 600; white-space: nowrap;"
                                                val amtColor = if (transaction.isDebit) t.expenseRed else t.incomeGreen
                                                val parentTx = transactions.find { it.id == transaction.parentTransactionId }
                                                val childAmt = if (transaction.amount < 0) "-$${(-transaction.amount).asDynamic().toFixed(2)}" else "$${transaction.amount.asDynamic().toFixed(2)}"
                                                span {
                                                    style = "color: $amtColor;"
                                                    +childAmt
                                                }
                                                if (parentTx != null) {
                                                    val parentAbs = if (parentTx.amount < 0) -parentTx.amount else parentTx.amount
                                                    span { style = "color: ${t.textSecondary};"; +" / " }
                                                    span { style = "color: $amtColor;"; +"$${parentAbs.asDynamic().toFixed(0)}" }
                                                }
                                            }
                                        } else {
                                            // Normal amount display
                                            span {
                                                style = """
                                                    font-size: 14px;
                                                    font-weight: 600;
                                                    color: ${if (transaction.isDebit) t.expenseRed else t.incomeGreen};
                                                """
                                                +if (transaction.amount < 0) "-$${(-transaction.amount).asDynamic().toFixed(2)}" else "$${transaction.amount.asDynamic().toFixed(2)}"
                                            }
                                        }
                                    }

                                    // Line 3: Goal (if assigned, light blue) or Category (full width)
                                    div {
                                        id = "txn-category-${transaction.id}"
                                        if (txnIdx == firstNonGoalIdx) attributes["data-guide"] = "category-text"
                                        style = """
                                            font-size: 13px;
                                            white-space: nowrap;
                                            overflow: hidden;
                                            text-overflow: ellipsis;
                                            color: ${if (transaction.hasGoalAssignment) "#66A3FF" else t.textSecondary};
                                            ${if (transaction.hasGoalAssignment) "font-weight: 500;" else ""}
                                        """
                                        if (transaction.hasGoalAssignment) {
                                            // Render the icon as SVG when it's a CategoryIconPaths id, emoji otherwise.
                                            val iconHtml = CategoryIconSvg.renderGoalIcon(
                                                iconValue = transaction.assignedGoalIcon ?: "🎯",
                                                size = 13,
                                                color = "#66A3FF"
                                            )
                                            val nameText = transaction.assignedGoalName ?: StringKey.ACTIVITY_GOAL.tr()
                                            // One transaction can fund several goals; the chip names
                                            // the largest, so a "+N" says the others exist rather than
                                            // leaving them invisible. Title lists them by name.
                                            val others = transaction.assignedGoals
                                                .filter { it.goalId != transaction.assignedGoalId }
                                            val moreHtml = if (others.isEmpty()) "" else {
                                                val names = others.joinToString(", ") { it.goalName }
                                                """<span title="${names.escapeHtml()}" style="opacity:0.75;font-size:11px;">+${others.size}</span>"""
                                            }
                                            unsafe { +"""<span style="display:inline-flex;align-items:center;gap:4px;vertical-align:middle;">$iconHtml<span>$nameText</span>$moreHtml</span>""" }
                                        } else {
                                            // Use shared CategoryUtils for formatted display (no UPPER_CASE)
                                            val categoryText = if (transaction.categoryLevel1.isNotEmpty() && transaction.categoryLevel2.isNotEmpty()) {
                                                utils.CategoryUtils.formatTwoLevelCategoryLocalized(
                                                    transaction.categoryLevel1,
                                                    transaction.categoryLevel2
                                                )
                                            } else {
                                                transaction.category
                                            }
                                            // For distributed children, append date range to differentiate groups
                                            if (transaction.isDistributedChild) {
                                                val parentTx = transactions.find { it.id == transaction.parentTransactionId }
                                                val dateRange = if (parentTx != null && parentTx.distributionStartMonth != null && parentTx.distributionEndMonth != null) {
                                                    val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                                                    val startParts = parentTx.distributionStartMonth!!.split("-")
                                                    val endParts = parentTx.distributionEndMonth!!.split("-")
                                                    val startLabel = "${monthNames[startParts[1].toInt() - 1]} ${startParts[0]}"
                                                    val endLabel = "${monthNames[endParts[1].toInt() - 1]} ${endParts[0]}"
                                                    " · $startLabel–$endLabel"
                                                } else ""
                                                +("$categoryText$dateRange")
                                            } else {
                                                +categoryText
                                            }
                                        }
                                    }
                                }
                            }

                            // Children are now rendered as part of the flat list above
                        }

                        // Sentinel for progressive rendering. The scroll listener
                        // (set up after render) bumps visibleTxnLimit when this gets
                        // near the viewport. Also clickable as a manual fallback
                        // when auto-scroll loading misfires (e.g. on mobile during
                        // inertia scroll).
                        if (sortedTransactions.size > visibleTxnLimit) {
                            val remaining = sortedTransactions.size - visibleTxnLimit
                            button {
                                id = "txn-list-sentinel"
                                style = """
                                    width: 100%;
                                    padding: 16px 24px;
                                    text-align: center;
                                    color: ${t.textSecondary};
                                    font-size: 13px;
                                    background: transparent;
                                    border: 1px dashed ${t.border};
                                    border-radius: 8px;
                                    cursor: pointer;
                                    margin-top: 8px;
                                """
                                onClickFunction = { loadMoreTxns() }
                                +"… load $remaining more"
                            }
                        }
                    }
                }
                } // End left column

                // Right column - Summary panel (desktop only) — sticky on scroll
                if (!isMobile) {
                    div {
                        id = "txn-right-sidebar"
                        style = """
                            display: flex;
                            flex-direction: column;
                            gap: 16px;
                            position: sticky;
                            top: 88px;
                            align-self: flex-start;
                            max-height: calc(100dvh - 88px);
                            overflow-y: auto;
                        """

                        // Filter to current calendar month only — used by cards below
                        val now = kotlin.js.Date()
                        val curMonth = now.getMonth()
                        val curYear = now.getFullYear()
                        val thisMonthTx = transactions.filter { tx ->
                            tx.date.getMonth() == curMonth && tx.date.getFullYear() == curYear
                        }
                        // Calculate spending (exclude split/distributed parents to avoid double-counting)
                        val totalSpent = thisMonthTx.filter { it.isDebit && !it.excludeFromTotals }.sumOf { -it.amount }
                        val totalIncome = thisMonthTx.filter { !it.isDebit && !it.excludeFromTotals }.sumOf { it.amount }

                        // Quick Stats card
                        div {
                            style = """
                                background: ${t.card};
                                border-radius: 12px;
                                padding: 20px;
                                border: 1px solid ${t.border};
                                box-shadow: ${t.cardShadow};
                            """
                            h3 {
                                style = """
                                    font-size: 12px;
                                    font-weight: 500;
                                    color: ${t.textSecondary};
                                    text-transform: uppercase;
                                    letter-spacing: 0.5px;
                                    margin: 0 0 16px 0;
                                """
                                +StringKey.ACTIVITY_THIS_MONTH.tr()
                            }

                            div {
                                style = """
                                    display: flex;
                                    justify-content: space-between;
                                    margin-bottom: 12px;
                                """
                                span { style = "color: ${t.textSecondary}; font-size: 13px;"; +StringKey.CATEGORY_INCOME.tr() }
                                span { style = "color: ${t.accent}; font-size: 14px; font-weight: 600;"; +"+$${totalIncome.asDynamic().toFixed(2)}" }
                            }
                            div {
                                style = """
                                    display: flex;
                                    justify-content: space-between;
                                    margin-bottom: 12px;
                                """
                                span { style = "color: ${t.textSecondary}; font-size: 13px;"; +StringKey.ACTIVITY_EXPENSES.tr() }
                                span { style = "color: ${t.expenseRed}; font-size: 14px; font-weight: 600;"; +"-$${totalSpent.asDynamic().toFixed(2)}" }
                            }
                            div {
                                style = """
                                    display: flex;
                                    justify-content: space-between;
                                    padding-top: 12px;
                                    border-top: 1px solid ${t.border};
                                """
                                span { style = "color: ${t.textSecondary}; font-size: 13px;"; +StringKey.ACTIVITY_NET.tr() }
                                val net = totalIncome - totalSpent
                                span {
                                    style = "color: ${if (net >= 0) t.accent else t.expenseRed}; font-size: 14px; font-weight: 600;"
                                    +"${if (net >= 0) "+" else ""}$${net.asDynamic().toFixed(2)}"
                                }
                            }
                        }

                        // Top Categories card
                        div {
                            style = """
                                background: ${t.card};
                                border-radius: 12px;
                                padding: 20px;
                                border: 1px solid ${t.border};
                                box-shadow: ${t.cardShadow};
                            """
                            h3 {
                                style = """
                                    font-size: 12px;
                                    font-weight: 500;
                                    color: ${t.textSecondary};
                                    text-transform: uppercase;
                                    letter-spacing: 0.5px;
                                    margin: 0 0 16px 0;
                                """
                                +StringKey.ACTIVITY_TOP_CATEGORIES.tr()
                            }

                            // Get top 5 categories by spending this month (exclude split/distributed parents)
                            val categoryTotals = thisMonthTx
                                .filter { it.isDebit && !it.excludeFromTotals }
                                .groupBy { tx ->
                                    if (tx.categoryLevel1.isNotEmpty()) {
                                        utils.CategoryUtils.getEffectiveDisplayCategory(
                                            tx.categoryLevel1,
                                            tx.categoryLevel2,
                                        )
                                    } else {
                                        tx.category.split(" → ").firstOrNull() ?: tx.category
                                    }
                                }
                                .mapValues { (_, txns) -> txns.sumOf { -it.amount } }
                                .entries
                                .sortedByDescending { it.value }
                                .take(5)

                            categoryTotals.forEach { (category, amount) ->
                                div {
                                    style = """
                                        display: flex;
                                        justify-content: space-between;
                                        margin-bottom: 10px;
                                    """
                                    span {
                                        style = "color: ${t.textPrimary}; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px;"
                                        +category
                                    }
                                    span { style = "color: ${t.textSecondary}; font-size: 13px;"; +"$${amount.asDynamic().toFixed(0)}" }
                                }
                            }
                        }

                        // Financial tip card (random tip from curated list)
                        div {
                            val tips = listOf(
                                "Set up automatic transfers to savings on payday — you can't spend what you don't see.",
                                "Review your subscriptions quarterly. Cancel anything you haven't used in 30 days.",
                                "The 50/30/20 rule: 50% needs, 30% wants, 20% savings. A simple starting point.",
                                "Track your spending for just one week — most people are surprised where their money goes.",
                                "Building an emergency fund? Start with \$1,000, then work toward 3 months of expenses.",
                                "Pay yourself first: save before spending, not after.",
                                "Avoid lifestyle inflation — when your income grows, grow your savings rate too.",
                                "Small daily expenses add up: \$5/day = \$1,825/year.",
                                "Set specific savings goals with deadlines — vague goals rarely get achieved.",
                                "Automate your bill payments to avoid late fees and protect your credit score.",
                            )
                            val tipIndex = (js("Date.now()") as Double).toLong() / 86400000 // Changes daily
                            val tip = tips[(tipIndex % tips.size).toInt()]

                            style = """
                                background: ${t.card};
                                border-radius: 12px;
                                padding: 20px;
                                border: 1px solid ${t.border};
                                box-shadow: ${t.cardShadow};
                            """
                            val bulbCircleBg = if (t.isDark) "rgba(0,217,255,0.22)" else "rgba(0,151,167,0.22)"
                            div {
                                style = "display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;"
                                div {
                                    style = "width: 36px; height: 36px; background: $bulbCircleBg; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
                                    span { style = "font-size: 18px;"; +"💡" }
                                }
                                p {
                                    style = "color: ${t.textPrimary}; font-size: 13px; line-height: 1.5; margin: 0;"
                                    +tip
                                }
                            }
                            p {
                                id = "tip-see-more"
                                style = "color: ${t.accent}; font-size: 12px; margin: 0; cursor: pointer; text-align: right;"
                                +"${StringKey.ACTIVITY_TIP_SEE_MORE.tr()} →"
                            }
                        }

                        // Savings recommendation slot — populated asynchronously with goals data
                        div {
                            id = "savings-recommendation-slot"
                        }
                    }
                }
            } // End main content grid
            } // End content wrapper with sidebar
            } // End else (not showDemoCard)
        }

        // Scroll listener for navbar shrink + sticky sidebar adjustment — only runs while this component is in the DOM
        if (!isMobile) {
            window.addEventListener("scroll", { _ ->
                if (!document.body!!.contains(container)) return@addEventListener
                // Clamp to 0 — macOS elastic overscroll can produce negative scrollY
                val scrollY = kotlin.math.max(0.0, window.scrollY)
                val navbar = document.getElementById("txn-navbar") as? HTMLElement
                val logo = document.getElementById("txn-logo") as? HTMLElement
                val spacer = document.getElementById("txn-navbar-spacer") as? HTMLElement
                val leftSidebar = document.getElementById("txn-left-sidebar") as? HTMLElement
                val rightSidebar = document.getElementById("txn-right-sidebar") as? HTMLElement

                // Interpolate: at scroll 0 → full size, at scroll 80 → compact
                val progress = (scrollY / 80.0).coerceIn(0.0, 1.0)

                // Navbar: padding shrinks from 16px to 8px vertical
                val vPad = 16 - (8 * progress)
                navbar?.style?.setProperty("padding", "${vPad}px 32px")

                // Logo: height shrinks from 56px to 28px
                val logoH = 56 - (28 * progress)
                logo?.style?.setProperty("height", "${logoH}px")

                // Update spacer to match current navbar height (2*vPad + logoH + 1px border)
                val spacerH = (2 * vPad + logoH + 1).toInt()
                spacer?.style?.setProperty("height", "${spacerH}px")

                // Adjust sticky sidebars to sit below the fixed navbar
                // Re-apply position:sticky to fix browser quirks after tab navigation
                leftSidebar?.style?.setProperty("position", "sticky")
                leftSidebar?.style?.setProperty("top", "${spacerH}px")
                leftSidebar?.style?.setProperty("max-height", "calc(100dvh - ${spacerH}px)")
                rightSidebar?.style?.setProperty("position", "sticky")
                rightSidebar?.style?.setProperty("top", "${spacerH + 24}px")
                rightSidebar?.style?.setProperty("max-height", "calc(100dvh - ${spacerH + 24}px)")
            })
        }

        // "See more tips" link → navigate to Goals page and scroll to tips
        container.querySelector("#tip-see-more")?.addEventListener("click", {
            onNavClick?.invoke("Goals")
            // Scroll to Financial Tips section after the Goals page renders
            kotlinx.browser.window.setTimeout({
                kotlinx.browser.document.getElementById("financialTipsContainer")?.asDynamic()?.scrollIntoView(
                    js("{ behavior: 'smooth', block: 'start' }")
                )
            }, 300)
        })

        // Hamburger button — capture overlay/backdrop refs scoped to THIS
        // container, NOT via document.getElementById. WebApp.renderTabContent
        // keeps stale tab containers in the DOM (display: none) for offline
        // caching, so multiple <div id="mobileMenuOverlay"> can coexist.
        // getElementById returns the first match — often a hidden tab's
        // overlay — so the click would set display:block on an element
        // inside a display:none parent and visually nothing would happen.
        // Same pattern Dashboard/Goals/Budget already use.
        if (isMobile) {
            val ownOverlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement
            val ownBackdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement
            container.querySelector("#txn-hamburger")?.addEventListener("click", { e ->
                e.preventDefault()
                e.stopPropagation()
                ownOverlay?.style?.display = "block"
                ownBackdrop?.style?.display = "block"
            })
        }

        // Mobile menu event listeners (shared three-zone layout)
        if (isMobile) {
            MarketingMenu.attachMobileEventListeners(
                container = container,
                onNavigate = { page ->
                    if (page == "guide") showGuideModal() else onNavClick?.invoke(page)
                },
                onAppNavigate = { tab ->
                    when (tab) {
                        "transactions" -> onNavClick?.invoke("Activity")
                        "dashboard" -> onNavClick?.invoke("Dashboard")
                        "goals" -> onNavClick?.invoke("Goals")
                        "budget" -> onNavClick?.invoke("Budget")
                        "settings" -> onSettingsClick?.invoke()
                        "logout" -> onNavClick?.invoke("logout")
                    }
                }
            )
        }

        // Load contexts for the sidebar section, then apply filter to transaction list.
        // If loadTransactions() already fetched contexts this load (the normal path),
        // reuse that result — don't hit /contexts a second time for the same data.
        MainScope().launch {
            renderContextsSidebar(refetch = !sidebarContextsLoaded)
            if (selectedSidebarContextIds.isNotEmpty()) {
                reRenderTransactionList()
            }
        }

        // Wire up the progressive-rendering observer once the container is in the DOM.
        // Defer to next tick so attachToDOM (or an in-place swap) has appended `container`.
        window.setTimeout({ setupLoadMoreObserver() }, 0)

        return container
    }

    private var sidebarContexts = listOf<repository.ContextApiModel>()
    private var contextSelectionLoaded = false
    // Set once loadTransactions() has successfully fetched contexts. Lets the
    // initial renderContextsSidebar() call (and every rerenderList() afterwards,
    // e.g. an account switch) reuse that result instead of re-fetching /contexts
    // a second time for identical data.
    private var sidebarContextsLoaded = false

    private suspend fun loadContextSelectionFromServer() {
        if (contextSelectionLoaded) return
        contextSelectionLoaded = true
        try {
            val saved = apiService.getSetting("selected_context_ids")
            if (!saved.isNullOrBlank()) {
                selectedSidebarContextIds.clear()
                selectedSidebarContextIds.addAll(saved.split(",").filter { it.isNotBlank() })
            }
        } catch (_: Exception) {}
    }

    private fun saveContextSelectionToServer() {
        val value = if (selectedSidebarContextIds.isEmpty()) "" else selectedSidebarContextIds.joinToString(",")
        MainScope().launch {
            try { apiService.setSetting("selected_context_ids", value) }
            catch (_: Exception) {}
        }
    }

    private suspend fun renderContextsSidebar(refetch: Boolean = true) {
        val t = WebTheme.current
        val section = document.getElementById("txn-contexts-section") ?: return
        if (refetch) {
            try {
                sidebarContexts = apiService.getContexts().contexts
                sidebarContextsLoaded = true
            } catch (_: Exception) {
                sidebarContexts = emptyList()
            }
        }
        // Load saved selection on first render
        loadContextSelectionFromServer()

        section.innerHTML = ""

        // Divider
        val divider = document.createElement("div") as HTMLElement
        divider.style.cssText = "border-top: 1px solid ${t.border}; margin: 16px 0 12px 0;"
        section.appendChild(divider)

        // Title row
        val titleRow = document.createElement("div") as HTMLElement
        titleRow.style.cssText = "display: flex; align-items: center; gap: 8px; margin-bottom: 10px;"

        val titleText = document.createElement("span") as HTMLElement
        titleText.textContent = StringKey.CONTEXT_TITLE.tr()
        titleText.style.cssText = "font-size: 12px; font-weight: 500; color: ${t.textSecondary}; text-transform: uppercase; letter-spacing: 0.5px;"
        titleRow.appendChild(titleText)

        // Contexts is a Pro feature — tag it for standard AND trialing users (a
        // trial-conversion nudge); only a permanent Pro subscriber sees nothing.
        if (showProTag()) {
            val proBadge = document.createElement("span") as HTMLElement
            proBadge.textContent = "PRO"
            proBadge.style.cssText = "background: ${t.proBadge}; color: ${t.proBadgeText}; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 4px; letter-spacing: 0.5px;"
            titleRow.appendChild(proBadge)
        }

        val spacer = document.createElement("div") as HTMLElement
        spacer.style.cssText = "flex: 1;"
        titleRow.appendChild(spacer)

        val addBtn = document.createElement("button") as HTMLElement
        addBtn.textContent = "+"
        addBtn.style.cssText = "width: 28px; height: 28px; border-radius: 6px; background: ${t.accent}; border: none; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 18px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center;"
        addBtn.addEventListener("click", {
            // Contexts are a Pro feature — bounce Standard users to the paywall
            // before showing the "name your context" prompt. The server enforces
            // the same rule (pro_required 403) but failing client-side is faster
            // and avoids creating a half-orphaned UI state on tier mismatch.
            if (!isProTier()) {
                onProUpgradeClick?.invoke()
                return@addEventListener
            }
            showAppPrompt(
                title = StringKey.CONTEXT_NAME_PLACEHOLDER.tr(),
                placeholder = StringKey.CONTEXT_NAME_PLACEHOLDER.tr(),
            ) { name, dialog ->
                MainScope().launch {
                    try {
                        apiService.createContext(name)
                        dialog.close()
                        renderContextsSidebar()
                    } catch (e: Exception) {
                        // Was only logged to the console before, so a failed
                        // create looked like nothing happening at all.
                        dialog.showError(e.message ?: "")
                    }
                }
            }
        })
        titleRow.appendChild(addBtn)
        section.appendChild(titleRow)

        // Chips row
        val chipsRow = document.createElement("div") as HTMLElement
        chipsRow.style.cssText = "display: flex; flex-wrap: wrap; gap: 6px;"

        // "All" chip — clickable, clears context filter
        val isAllSelected = selectedSidebarContextIds.isEmpty()
        val allChip = document.createElement("div") as HTMLElement
        allChip.textContent = StringKey.CONTEXT_ALL.tr()
        allChip.style.cssText = if (isAllSelected) {
            "background: ${t.chipSelectedBg}; border: 1px solid ${t.chipSelectedBorder}; border-radius: 16px; padding: 4px 12px; color: ${t.textPrimary}; font-size: 12px; font-weight: 500; cursor: pointer;"
        } else {
            "background: ${t.chipUnselectedBg}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 16px; padding: 4px 12px; color: ${t.chipUnselectedText}; font-size: 12px; font-weight: 500; cursor: pointer;"
        }
        allChip.addEventListener("click", {
            selectedSidebarContextIds.clear()
            saveContextSelectionToServer()
            MainScope().launch { renderContextsSidebar() }
            reRenderTransactionList()
        })
        chipsRow.appendChild(allChip)

        // Context chips — clickable to toggle filter
        sidebarContexts.forEach { ctx ->
            val isSelected = ctx.id in selectedSidebarContextIds
            val chip = document.createElement("div") as HTMLElement
            chip.style.cssText = if (isSelected) {
                "position: relative; background: ${hexToRgba(ctx.color, 0.15)}; border: 1px solid ${ctx.color}; border-radius: 16px; padding: 4px 10px; display: flex; align-items: center; gap: 5px; cursor: pointer;"
            } else {
                // No chip-wide opacity: that would dim the colour dot too, making it
                // look duller than the identical dot on the transaction rows. Dim the
                // label instead (below) so the swatch always shows the true colour.
                "position: relative; background: ${t.chipUnselectedBg}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 16px; padding: 4px 10px; display: flex; align-items: center; gap: 5px; cursor: pointer;"
            }

            val dot = document.createElement("div") as HTMLElement
            dot.style.cssText = "width: 7px; height: 7px; border-radius: 50%; background: ${ctx.color}; flex-shrink: 0;"
            chip.appendChild(dot)

            val nameSpan = document.createElement("span") as HTMLElement
            nameSpan.textContent = ctx.name
            nameSpan.style.cssText = "color: ${if (isSelected) t.textPrimary else t.chipUnselectedText}; font-size: 12px;"
            chip.appendChild(nameSpan)

            // Click to toggle context filter
            chip.addEventListener("click", {
                if (ctx.id in selectedSidebarContextIds) {
                    selectedSidebarContextIds.remove(ctx.id)
                } else {
                    selectedSidebarContextIds.add(ctx.id)
                }
                saveContextSelectionToServer()
                MainScope().launch { renderContextsSidebar() }
                reRenderTransactionList()
            })

            // Hover: reveal edit/delete. The icons are always in the layout and
            // only their VISIBILITY toggles — `display: none` would take them out
            // of it, so the chip was narrower unhovered and grew on hover. A chip
            // near the end of a row could then no longer fit, wrapped to the next
            // line, slid out from under the cursor, lost the hover, shrank back,
            // fitted again, and flickered between the two rows indefinitely.
            // Reserving the width means hovering never changes layout.
            val actions = document.createElement("span") as HTMLElement
            actions.style.cssText = "display: inline-flex; visibility: hidden; margin-left: 4px; gap: 2px;"
            actions.innerHTML = "<span class='ctx-edit' style='cursor:pointer;font-size:11px;color:#888;'>✏️</span><span class='ctx-del' style='cursor:pointer;font-size:11px;color:#888;'>✕</span>"
            chip.appendChild(actions)

            chip.addEventListener("mouseenter", { actions.style.visibility = "visible" })
            chip.addEventListener("mouseleave", { actions.style.visibility = "hidden" })

            actions.querySelector(".ctx-edit")?.addEventListener("click", { e ->
                e.stopPropagation()
                showContextEditDialog(ctx)
            })

            actions.querySelector(".ctx-del")?.addEventListener("click", { e ->
                e.stopPropagation()
                showAppConfirm(
                    title = StringKey.CONTEXT_DELETE_CONFIRM.tr().replace("%s", ctx.name),
                    confirmLabel = StringKey.ACTION_DELETE.tr(),
                    destructive = true,
                ) {
                    MainScope().launch {
                        try { apiService.deleteContext(ctx.id); renderContextsSidebar() }
                        catch (ex: Exception) { console.log("Failed to delete: ${ex.message}") }
                    }
                }
            })

            chipsRow.appendChild(chip)
        }

        section.appendChild(chipsRow)
    }
    /**
     * Rename an account.
     *
     * Laid out the way the rename reads: the account as it is called now, its
     * institution, then the action, then the field. Naming the subject is the
     * thing `window.prompt` could never do — it shows one line, so the dialog
     * asked "your name for this account" about nothing in particular, and
     * ACCOUNT_RENAME_HINT had nowhere to go.
     */
    private fun showAccountRenameDialog(account: PlaidAccount) {
        showAppPrompt(
            title = models.AccountDisplayName.primary(
                account.custom_name, account.official_name, account.name, account.mask,
            ),
            subtitle = account.institution_name,
            label = StringKey.ACCOUNT_RENAME.tr(),
            initial = account.custom_name ?: "",
            placeholder = StringKey.ACCOUNT_RENAME_PLACEHOLDER.tr(),
            hint = StringKey.ACCOUNT_RENAME_HINT.tr(),
            // Clearing the field is how you go back to the bank's own name, so
            // an empty value is a real answer here rather than a missing one.
            allowEmpty = true,
        ) { entered, dialog ->
            MainScope().launch {
                try {
                    val stored = apiService.updateAccountCustomName(account.account_id, entered)
                    val idx = accounts.indexOfFirst { it.account_id == account.account_id }
                    if (idx >= 0) accounts[idx] = accounts[idx].copy(custom_name = stored)
                    dialog.close()
                    rerenderList()
                } catch (ex: Exception) {
                    dialog.showError(
                        if (ex is AccountNameError) accountRenameErrorText(ex, entered) else ex.message ?: "",
                    )
                }
            }
        }
    }


    private fun showContextEditDialog(ctx: repository.ContextApiModel) {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = "position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; display: flex; align-items: center; justify-content: center;"

        val dialog = document.createElement("div") as HTMLElement
        dialog.style.cssText = "background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 340px; width: 90%; color: ${t.textPrimary};"

        // Title
        val title = document.createElement("h3") as HTMLElement
        title.textContent = StringKey.CONTEXT_EDIT_TITLE.tr()
        title.style.cssText = "margin: 0 0 16px; font-size: 16px; font-weight: 600; color: ${t.textPrimary};"
        dialog.appendChild(title)

        // Name input
        val nameLabel = document.createElement("div") as HTMLElement
        nameLabel.textContent = StringKey.CONTEXT_NAME_PLACEHOLDER.tr()
        nameLabel.style.cssText = "font-size: 12px; color: ${t.textSecondary}; margin-bottom: 4px;"
        dialog.appendChild(nameLabel)

        val nameInput = document.createElement("input") as org.w3c.dom.HTMLInputElement
        nameInput.value = ctx.name
        nameInput.style.cssText = "width: 100%; padding: 10px 12px; background: ${t.background}; border: 1px solid ${t.border}; border-radius: 8px; color: ${t.textPrimary}; font-size: 14px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
        dialog.appendChild(nameInput)

        // Color label
        val colorLabel = document.createElement("div") as HTMLElement
        colorLabel.textContent = StringKey.CATEGORY_COLOUR.tr()
        colorLabel.style.cssText = "font-size: 12px; color: ${t.textSecondary}; margin-bottom: 8px;"
        dialog.appendChild(colorLabel)

        // Color palette grid
        var selectedColor = ctx.color
        val palette = utils.CategoryUtils.colorPalette

        val grid = document.createElement("div") as HTMLElement
        grid.style.cssText = "display: grid; grid-template-columns: repeat(6, 28px); gap: 6px; justify-content: center; margin-bottom: 20px;"

        fun updateSwatches() {
            val swatches = grid.children
            for (i in 0 until swatches.length) {
                val s = swatches.item(i) as? HTMLElement ?: continue
                val c = palette.getOrNull(i) ?: continue
                val sel = c.equals(selectedColor, ignoreCase = true)
                s.style.border = if (sel) "2px solid #fff" else "2px solid transparent"
                s.innerHTML = if (sel) """<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>""" else ""
            }
        }

        palette.forEach { color ->
            val swatch = document.createElement("div") as HTMLElement
            val isSelected = color.equals(selectedColor, ignoreCase = true)
            swatch.style.cssText = """
                width: 28px; height: 28px; border-radius: 50%;
                background: $color; cursor: pointer;
                border: 2px solid ${if (isSelected) "#fff" else "transparent"};
                display: flex; align-items: center; justify-content: center;
            """
            if (isSelected) {
                swatch.innerHTML = """<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>"""
            }
            swatch.addEventListener("click", {
                selectedColor = color
                updateSwatches()
            })
            grid.appendChild(swatch)
        }
        dialog.appendChild(grid)

        // Buttons row
        val btnRow = document.createElement("div") as HTMLElement
        btnRow.style.cssText = "display: flex; gap: 8px; justify-content: flex-end;"

        val cancelBtn = document.createElement("button") as HTMLElement
        cancelBtn.textContent = StringKey.ACTION_CANCEL.tr()
        cancelBtn.style.cssText = "padding: 8px 16px; background: ${t.border}; border: none; border-radius: 8px; color: ${t.textSecondary}; cursor: pointer; font-size: 13px;"
        cancelBtn.addEventListener("click", { overlay.remove() })
        btnRow.appendChild(cancelBtn)

        val saveBtn = document.createElement("button") as HTMLElement
        saveBtn.textContent = StringKey.ACTION_SAVE.tr()
        saveBtn.style.cssText = "padding: 8px 16px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; cursor: pointer; font-size: 13px; font-weight: 600;"
        saveBtn.addEventListener("click", {
            val newName = nameInput.value.trim()
            if (newName.isNotEmpty()) {
                overlay.remove()
                val colorChanged = !selectedColor.equals(ctx.color, ignoreCase = true)
                MainScope().launch {
                    try {
                        apiService.updateContext(ctx.id, newName, if (colorChanged) selectedColor else null)
                        renderContextsSidebar()
                    } catch (ex: Exception) {
                        console.log("Failed to update context: ${ex.message}")
                    }
                }
            }
        })
        btnRow.appendChild(saveBtn)
        dialog.appendChild(btnRow)

        overlay.appendChild(dialog)
        overlay.addEventListener("click", { e -> if (e.target == overlay) overlay.remove() })
        document.body?.appendChild(overlay)
        nameInput.focus()
    }

    /**
     * Renders the two cascading category-filter selects (primary + optional
     * sub-category) plus a clear button. Options come from codes observed
     * in the user's transactions, sorted by frequency. Selecting a primary
     * narrows to that primary; picking a sub narrows further.
     *
     * Filter changes do NOT re-render the whole list — they hide/show rows
     * in place via [applyCategoryFilterToDom], same pattern the context
     * filter uses.
     */
    /** Layout: two stacked rows.
     *  Row 1: "FILTERED BY:" label + primary <select>.
     *  Row 2: Clear button (left, hidden when no filter active) + sub-category <select> (right).
     *  This puts the sub-cat directly under the primary it depends on, with
     *  Clear sitting on its own row when active. Mobile-friendly. */
    private fun renderCategoryFilterPicker(parent: FlowContent, t: theme.ThemeColors) {
        val primaryFreq = mutableMapOf<String, Int>()
        transactions.forEach { tx ->
            val code = tx.categoryLevel1
            if (code.isNotBlank()) primaryFreq[code] = (primaryFreq[code] ?: 0) + 1
        }
        val orderedPrimaries = primaryFreq.entries
            .sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }
                .thenBy { utils.CategoryUtils.formatCategoryNameLocalized(it.key) })
            .map { it.key }
            .let { codes ->
                // Always include the currently-selected primary even if the
                // loaded account has zero txns in it — otherwise the dropdown
                // can't display the active filter and silently falls back to
                // "All Categories", hiding the fact that a filter is applied
                // (which is why the list looks empty).
                val active = categoryFilter
                if (!active.isNullOrBlank() && codes.none { it.equals(active, ignoreCase = true) }) {
                    listOf(active) + codes
                } else {
                    codes
                }
            }
        val orderedSubs: List<String> = categoryFilter?.let { primary ->
            val freq = mutableMapOf<String, Int>()
            transactions.forEach { tx ->
                if (tx.categoryLevel1.equals(primary, ignoreCase = true) &&
                    tx.categoryLevel2.isNotBlank()) {
                    freq[tx.categoryLevel2] = (freq[tx.categoryLevel2] ?: 0) + 1
                }
            }
            freq.entries
                .sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }
                    .thenBy { it.key })
                .map { it.key }
        } ?: emptyList()

        val selectStyle = """
            background: ${t.card};
            border: 1px solid ${t.border};
            border-radius: 8px;
            padding: 8px 10px;
            color: ${t.textPrimary};
            font-size: 13px;
            cursor: pointer;
            min-width: 140px;
        """
        parent.div {
            style = "display: flex; flex-direction: column; gap: 6px; padding: 8px 12px;"
            // Row 1: label + primary select.
            div {
                style = "display: flex; align-items: center; gap: 8px; flex-wrap: wrap;"
                span {
                    style = "color: ${t.textSecondary}; font-size: 12px; letter-spacing: 0.4px; text-transform: uppercase;"
                    +StringKey.ACTIVITY_FILTERED_BY.tr()
                }
                select {
                    id = "cat-primary-select"
                    style = selectStyle
                    onChangeFunction = { ev ->
                        val v = (ev.target as? org.w3c.dom.HTMLSelectElement)?.value.orEmpty()
                        categoryFilter = v.takeIf { it.isNotEmpty() }
                        // Sub from the previous primary doesn't belong to the new one.
                        subCategoryFilter = null
                        if (categoryFilter == null) onCategoryFilterCleared?.invoke()
                        // Repopulate the sub-cat <select> with the new primary's
                        // observed sub-categories.
                        refreshSubCategoryOptions(categoryFilter)
                        setCategoryClearVisibility()
                        applyCategoryFilterToDom()
                    }
                    option {
                        value = ""
                        if (categoryFilter == null) selected = true
                        +StringKey.CATEGORY_ALL_CATEGORIES.tr()
                    }
                    orderedPrimaries.forEach { code ->
                        option {
                            value = code
                            if (categoryFilter?.equals(code, ignoreCase = true) == true) selected = true
                            +utils.CategoryUtils.formatCategoryNameLocalized(code)
                        }
                    }
                }
            }
            // Row 2: Clear button (left, hidden when no filter active) + sub-category select.
            div {
                style = "display: flex; align-items: center; gap: 8px; flex-wrap: wrap;"
                // Clear button is always rendered (so it can appear/disappear
                // after dropdown changes without re-rendering the whole picker).
                button {
                    id = "cat-clear-button"
                    val initiallyVisible = categoryFilter != null || subCategoryFilter != null
                    style = """
                        background: transparent; border: 1px solid ${t.border};
                        border-radius: 8px; padding: 6px 12px; cursor: pointer;
                        color: ${t.textSecondary}; font-size: 13px;
                        display: ${if (initiallyVisible) "inline-flex" else "none"};
                    """
                    onClickFunction = {
                        categoryFilter = null
                        subCategoryFilter = null
                        onCategoryFilterCleared?.invoke()
                        refreshSubCategoryOptions(null)
                        applyCategoryFilterToDom()
                        (document.getElementById("cat-primary-select") as? org.w3c.dom.HTMLSelectElement)?.value = ""
                        setCategoryClearVisibility()
                    }
                    +StringKey.ACTION_CLEAR.tr()
                }
                // Sub-category select — always present, disabled when no
                // primary is chosen (then the placeholder reads "All
                // Subcategories" and the dropdown is greyed).
                select {
                    id = "cat-sub-select"
                    style = selectStyle + if (categoryFilter == null) "opacity: 0.5;" else ""
                    disabled = categoryFilter == null
                    onChangeFunction = { ev ->
                        val v = (ev.target as? org.w3c.dom.HTMLSelectElement)?.value.orEmpty()
                        subCategoryFilter = v.takeIf { it.isNotEmpty() }
                        setCategoryClearVisibility()
                        applyCategoryFilterToDom()
                    }
                    option {
                        value = ""
                        if (subCategoryFilter == null) selected = true
                        +StringKey.CATEGORY_ALL_SUBCATEGORIES.tr()
                    }
                    orderedSubs.forEach { detailedCode ->
                        option {
                            value = detailedCode
                            if (subCategoryFilter?.equals(detailedCode, ignoreCase = true) == true) selected = true
                            val subOnly = detailedCode.removePrefix("${categoryFilter ?: ""}_")
                            +utils.CategoryUtils.formatCategoryNameLocalized(subOnly)
                        }
                    }
                }
            }
        }
    }

    /**
     * Toggles the Clear button between visible / hidden via inline display
     * style. The picker DOM isn't re-rendered on each filter change, so we
     * mutate the existing button in place.
     */
    private fun setCategoryClearVisibility() {
        val btn = document.getElementById("cat-clear-button") as? HTMLElement ?: return
        val show = categoryFilter != null || subCategoryFilter != null
        btn.style.display = if (show) "inline-flex" else "none"
    }

    /**
     * Rebuilds the sub-category <select> options for the given primary. Called
     * when the user changes the primary dropdown — without this the sub-cat
     * select stays frozen with whatever was computed at first render (usually
     * empty, since the primary was unset initially).
     */
    private fun refreshSubCategoryOptions(primary: String?) {
        val sel = document.getElementById("cat-sub-select") as? org.w3c.dom.HTMLSelectElement ?: return
        // Wipe existing options.
        while (sel.firstChild != null) sel.removeChild(sel.firstChild!!)
        // Always include "All Subcategories" first.
        sel.appendChild(
            (document.createElement("option") as org.w3c.dom.HTMLOptionElement).apply {
                value = ""
                text = StringKey.CATEGORY_ALL_SUBCATEGORIES.tr()
                selected = subCategoryFilter == null
            }
        )
        if (primary != null) {
            val freq = mutableMapOf<String, Int>()
            transactions.forEach { tx ->
                if (tx.categoryLevel1.equals(primary, ignoreCase = true) &&
                    tx.categoryLevel2.isNotBlank()) {
                    freq[tx.categoryLevel2] = (freq[tx.categoryLevel2] ?: 0) + 1
                }
            }
            val sorted = freq.entries
                .sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }
                    .thenBy { it.key })
                .map { it.key }
            sorted.forEach { detailedCode ->
                val subOnly = detailedCode.removePrefix("${primary}_")
                sel.appendChild(
                    (document.createElement("option") as org.w3c.dom.HTMLOptionElement).apply {
                        value = detailedCode
                        text = utils.CategoryUtils.formatCategoryNameLocalized(subOnly)
                        selected = subCategoryFilter?.equals(detailedCode, ignoreCase = true) == true
                    }
                )
            }
        }
        sel.disabled = primary == null
        sel.style.opacity = if (primary == null) "0.5" else "1"
    }

    /**
     * Applies the current category + sub-category filter to the rendered DOM
     * by hiding/showing rows. Reads the `data-cat-primary` / `data-cat-detailed`
     * attributes written on each row. Also runs through the context filter so
     * a category change doesn't override an active context selection.
     */
    private fun applyCategoryFilterToDom() {
        val rows = document.querySelectorAll("[data-txn-id]")
        val ctxFiltered: Set<String>? = if (selectedSidebarContextIds.isEmpty()) null
        else transactions.filter { txn ->
            val txnCtx = txn.contextIds.toSet()
            selectedSidebarContextIds.all { it in txnCtx }
        }.map { it.id }.toSet()

        val catFilter = categoryFilter
        val subFilter = subCategoryFilter
        for (i in 0 until rows.length) {
            val row = rows.item(i) as? HTMLElement ?: continue
            val txnId = row.getAttribute("data-txn-id") ?: continue
            val primary = row.getAttribute("data-cat-primary").orEmpty()
            val detailed = row.getAttribute("data-cat-detailed").orEmpty()
            val ctxOk = ctxFiltered == null || txnId in ctxFiltered
            val primaryOk = catFilter == null || primary.equals(catFilter, ignoreCase = true)
            val subOk = subFilter == null || detailed.equals(subFilter, ignoreCase = true)
            row.style.display = if (ctxOk && primaryOk && subOk) "flex" else "none"
        }
    }

    private fun reRenderTransactionList() {
        val t = WebTheme.current
        // Show/hide transaction rows based on context filter — no re-fetch needed
        val allRows = document.querySelectorAll("[data-txn-id]")
        val filteredTxns = if (selectedSidebarContextIds.isEmpty()) null
        else transactions.filter { txn ->
            val txnCtx = txn.contextIds.toSet()
            selectedSidebarContextIds.all { it in txnCtx }
        }
        val matchingIds = filteredTxns?.map { it.id }?.toSet()

        for (i in 0 until allRows.length) {
            val row = allRows.item(i) as? HTMLElement ?: continue
            val txnId = row.getAttribute("data-txn-id") ?: continue
            row.style.display = if (matchingIds == null || txnId in matchingIds) "flex" else "none"
        }

        // Update context total line
        val totalLine = document.getElementById("context-total-line") as? HTMLElement
        if (totalLine != null) {
            if (filteredTxns != null) {
                val total = filteredTxns.sumOf { it.amount }
                val contextNames = sidebarContexts
                    .filter { it.id in selectedSidebarContextIds }
                    .joinToString(" + ") { it.name }
                val color = if (total >= 0) t.incomeGreen else t.expenseRed
                val sign = if (total >= 0) "+" else "-"
                val formatted = "$sign$${kotlin.math.abs(total).asDynamic().toFixed(2)}"
                totalLine.style.cssText = "margin-top: 8px; padding: 6px 12px; background: rgba(0,217,255,0.08); border: 1px solid rgba(0,217,255,0.2); border-radius: 8px; display: inline-flex; align-items: center; gap: 6px;"
                totalLine.innerHTML = """<span style="color: ${t.accent}; font-size: 12px;">$contextNames:</span> <span style="color: $color; font-size: 14px; font-weight: 600;">$formatted</span>"""
            } else {
                totalLine.style.display = "none"
                totalLine.innerHTML = ""
            }
        }
    }

    private fun hexToRgba(hex: String, alpha: Double): String {
        val cleanHex = hex.trimStart('#')
        val r = cleanHex.substring(0, 2).toInt(16)
        val g = cleanHex.substring(2, 4).toInt(16)
        val b = cleanHex.substring(4, 6).toInt(16)
        return "rgba($r,$g,$b,$alpha)"
    }

    fun getTransactions(): List<Transaction> = transactions

    /** Current primary category filter (the value shown selected in the
     *  dropdown). Used by the host to carry the filter across account-switch
     *  re-renders so a Wiz 3R-applied filter — or one the user picked manually
     *  after landing — survives clicking a different account, and clears only
     *  when the user hits the explicit Clear button. */
    fun getCategoryFilter(): String? = categoryFilter

    fun setRecommendationHtml(html: String) {
        document.getElementById("savings-recommendation-slot")?.innerHTML = html
    }

    suspend fun loadTransactions(forceRefresh: Boolean = false) {
        try {
            console.log("Loading transactions from server...")
            // Demo: once loaded, keep local state — demo writes are local-only and
            // never persist, so re-fetching would wipe the user's changes. (A
            // reload of the page ends the demo and resets it.)
            if (ReadOnlyGate.demoMode && transactions.isNotEmpty()) {
                console.log("🎭 Demo: keeping local transactions instead of re-fetching")
                return
            }
            // Load contexts up front so each row's context dots have their colours
            // the moment the rows are built — rows render synchronously right after
            // this suspend fn returns, and the sidebar's own context fetch lands too
            // late to colour them on first paint.
            try {
                sidebarContexts = apiService.getContexts().contexts
                sidebarContextsLoaded = true
            } catch (_: Exception) {}
            // Reuse the host's already-fetched response on first load (see
            // preloadedResponse) instead of hitting the endpoint again for the
            // identical data. Any later call on this instance (e.g. the manual
            // "Sync" button) always fetches fresh, since pendingPreloadedResponse
            // is consumed (nulled) the first time it's used.
            val preloaded = pendingPreloadedResponse
            val response = if (preloaded != null) {
                pendingPreloadedResponse = null
                preloaded
            } else {
                // Genuine network fetch (first real load, or a forced "Sync").
                // Invalidate the per-account memo so refreshed data isn't
                // shadowed by a stale list from a previous visit to this
                // account — the memo exists only to bridge network-free
                // switchAccount() hops, never to survive a refetch. Any
                // in-place edit already persisted to the server before
                // mutating locally, so this refetch's data supersedes it.
                val fetched = apiService.getTransactionsFromServer(forceRefresh = forceRefresh)
                transactionsByAccount.clear()
                fetched
            }
            console.log("Got response:", response)
            lastResponse = response

            applyResponseToState(response)
            console.log("Loaded ${transactions.size} transactions")

            updateStatusLine(getStatusText())

            // Update refresh button to reflect current relink state
            val refreshBtn = document.getElementById("refresh-button") as? HTMLElement
            val t = WebTheme.current
            refreshBtn?.let {
                if (needsRelink) {
                    it.textContent = StringKey.ACCOUNT_RELINK_BUTTON.tr()
                    it.style.color = t.targetOrange
                    it.style.borderColor = t.targetOrange
                } else {
                    it.textContent = StringKey.ACTIVITY_SYNC.tr()
                    it.style.color = t.textSecondary
                    it.style.borderColor = t.border
                }
            }

            // Load user custom category colors
            try {
                val colorJson = apiService.getSetting("custom_category_colors")
                if (!colorJson.isNullOrBlank()) {
                    userCategoryColors = kotlinx.serialization.json.Json.decodeFromString(colorJson)
                }
            } catch (_: Exception) {}
        } catch (e: Exception) {
            console.error("Failed to load transactions:", e)
            console.error("Error details:", e.message)
            if (e.message == "AUTH_REQUIRED") {
                console.log("Authentication required - triggering logout")
                onAuthError()
                return
            } else {
                console.error("Unexpected error loading transactions")
                accountName = StringKey.ACTIVITY_ERROR_LOADING.tr()
                accountBalance = 0.0
                // `transactions` may be the same list object memoized in
                // transactionsByAccount (aliased there by applyResponseToState).
                // Clearing it in place would poison the memo permanently for
                // this account — a later switchAccount() back to it would
                // memo-hit the now-emptied list forever. Drop the memo entry
                // instead so the next successful load rebuilds it, and point
                // this instance at a fresh empty list for the blank error state.
                transactionsByAccount.remove(accountId)
                transactions = mutableListOf()
            }
        }
    }

    /**
     * Switches the currently displayed account using data already retained from
     * the last full load (`lastResponse`) — no network calls. Re-derives (or
     * reuses a previously memoized, possibly locally-edited) per-account
     * transaction list, resets transient per-account UI state the same way a
     * fresh component instance used to, and re-renders in place via
     * rerenderList(). The sticky category filter (`categoryFilter`) and the
     * "Show pending holds" opt-ins (`showPendingHoldsAccounts`) are intentionally
     * NOT reset here — they persist across accounts, same as before.
     *
     * Runs inside MainScope().launch because the shared row-mapping helper
     * (mapPlaidTransactions) is `suspend` — it can fall back to AI categorization
     * for rows the server didn't categorize. On JS that's a same-tick coroutine
     * resume in the overwhelmingly common case (server categorization present),
     * not a network round trip, so this is still an in-memory, no-fetch switch.
     */
    fun switchAccount(newAccountId: String) {
        accountId = newAccountId
        selectedTransactionIds.clear()
        visibleTxnLimit = 250
        subCategoryFilter = null
        val response = lastResponse
        if (response != null) {
            MainScope().launch {
                applyResponseToState(response)
                rerenderList()
            }
        } else {
            rerenderList()
        }
    }

    /**
     * Recomputes every field derived from a full (all-accounts) /transactions
     * response for the currently selected `accountId`: account name/balance,
     * per-account hidden-hold counts, the mapped transaction list, relink state,
     * and last-sync timestamp. Shared by loadTransactions() (after a real fetch)
     * and switchAccount() (reusing the retained response) so both paths derive
     * state identically.
     */
    private suspend fun applyResponseToState(response: TransactionsResponse) {
        // Update account info
        val account = if (accountId != null) {
            response.accounts.find { it.account_id == accountId }
        } else {
            response.accounts.firstOrNull()
        }

        accountName = when {
            account != null -> models.AccountDisplayName.primary(
                account.custom_name, account.official_name, account.name, account.mask,
            )
            response.transactions.any { it.transaction_id.startsWith("demo-") } -> StringKey.ACTIVITY_DEMO_ACCOUNT.tr()
            else -> "Connected Account"
        }
        console.log("Account name:", accountName)
        console.log("First transaction:", response.transactions.firstOrNull()?.name)

        // Use real balance from Plaid API response
        accountBalance = account?.balance_current
            ?: account?.balance_available
            ?: 0.0
        console.log("Using real Plaid balance: $accountBalance")

        // Per-account hidden-hold counts, from the FULL response (all accounts,
        // before the per-account display filter). Drives the "Show pending holds (N)"
        // checkbox under each account card.
        hiddenHoldCountByAccount = response.transactions
            .filter { it.is_hidden }
            .groupingBy { it.account_id }
            .eachCount()

        // Per-account mapped transaction list. Memoized by account_id so
        // switching back to an already-visited account reuses its (possibly
        // locally-edited) list instead of re-deriving a pristine copy from the
        // raw response, which would silently discard in-place edits.
        val cacheKey = accountId
        val mappedTransactions = if (cacheKey != null) {
            transactionsByAccount.getOrPut(cacheKey) { mapPlaidTransactions(response, cacheKey) }
        } else {
            mapPlaidTransactions(response, null)
        }

        // Guard against interleaved switchAccount() calls: mapPlaidTransactions
        // can suspend (categorizeTransactionWithAI fallback). If the user
        // clicked to a different account while this call was suspended,
        // `accountId` has since moved on — the account we captured (cacheKey)
        // is no longer the one on screen, so discard these now-stale results
        // rather than overwriting whatever the newer call already rendered.
        if (cacheKey != accountId) return
        transactions = mappedTransactions

        // Track relink state from selected account (not global)
        val selectedAccount = response.accounts.find { it.account_id == accountId }
        needsRelink = selectedAccount?.needs_relink ?: response.needs_relink

        // Use server's last_sync_time if available, otherwise fall back to now
        lastUpdateTimestamp = if (response.last_sync_time != null) {
            val syncTime = response.last_sync_time
            val normalized = if (!syncTime.endsWith("Z")) "${syncTime}Z" else syncTime
            val parsed = Date(normalized).getTime()
            if (parsed.isNaN()) Date.now() else parsed
        } else {
            Date.now()
        }
        bankName = accountName // Use the account name as the bank name
    }

    /**
     * Filters the full (all-accounts) response down to [forAccountId] (or all,
     * if null) and maps each PlaidTransaction to the UI's Transaction model.
     * Pulled out of applyResponseToState() so it can be memoized per-account.
     */
    private suspend fun mapPlaidTransactions(response: TransactionsResponse, forAccountId: String?): MutableList<Transaction> {
        val filteredTransactions = if (forAccountId != null) {
            response.transactions.filter { it.account_id == forAccountId }
        } else {
            response.transactions
        }

        return filteredTransactions.map { plaidTx ->
                val amount = -plaidTx.amount // Plaid amounts are positive for debits
                val isDebit = plaidTx.amount > 0
                
                // Parse date as local date to avoid timezone shift. Defensive
                // against malformed/legacy strings — a single bad row used to
                // throw NumberFormatException and surface as "Error loading
                // data" for the whole account. Now we fall back to epoch and
                // log; the row still renders, just at the bottom of the list.
                val localDate = try {
                    val parts = plaidTx.date.split("-")
                    if (parts.size >= 3) {
                        val year = parts[0].toInt()
                        val month = parts[1].toInt() - 1 // JS months 0-based
                        val day = parts[2].toInt()
                        Date(year, month, day)
                    } else {
                        // Last-resort: let JS parse it (handles ISO-ish strings)
                        Date(plaidTx.date)
                    }
                } catch (e: Throwable) {
                    console.warn("⚠️ Unparseable transaction date '${plaidTx.date}' for ${plaidTx.transaction_id}; using epoch as fallback")
                    Date(0)
                }
                
                if (isDebit) {
                    // Use server's hybrid categorization if available
                    val serverCategory = plaidTx.personal_finance_category
                    val serverSource = plaidTx.category_source

                    // DEBUG: Log categorization data from server
                    console.log("🔍 DEBUG: Transaction '${plaidTx.name}' - serverCategory=${serverCategory?.primary}/${serverCategory?.detailed}, serverSource=$serverSource")

                    if (serverCategory != null) {
                        // Server provided categorization - use it regardless of source
                        val displaySource = when (serverSource) {
                            "pattern" -> "pattern_match"
                            "cache" -> "pattern_match"
                            "llm" -> "ai_categorization"
                            "plaid" -> "plaid"
                            "user" -> "user"
                            "split" -> "split"
                            else -> "plaid"  // Default to plaid if source is null/unknown
                        }
                        Transaction(
                            id = plaidTx.transaction_id,
                            merchant = plaidTx.name ?: plaidTx.merchant_name ?: StringKey.ACTIVITY_UNKNOWN.tr(),
                            amount = amount,
                            date = localDate,
                            category = utils.CategoryUtils.formatTwoLevelCategoryLocalized(serverCategory.primary, serverCategory.detailed),
                            account = accountName,
                            isDebit = isDebit,
                            aiConfidence = null, // Display uses confidenceLevel, not this
                            categorySource = displaySource,
                            categoryLevel1 = serverCategory.primary,
                            categoryLevel2 = serverCategory.detailed,
                            categoryLevel3 = "",
                            confidenceLevel = serverCategory.confidence_level,
                            // Split/Distribute fields from server
                            parentTransactionId = plaidTx.parent_transaction_id,
                            childType = plaidTx.child_type,
                            isSplitParent = plaidTx.is_split_parent,
                            isDistributedParent = plaidTx.is_distributed_parent,
                            splitOrder = plaidTx.split_order,
                            distributionStartMonth = plaidTx.distribution_start_month,
                            distributionEndMonth = plaidTx.distribution_end_month,
                            distributionMonthIndex = plaidTx.distribution_month_index,
                            distributionTotalMonths = plaidTx.distribution_total_months,
                            // Goal assignment fields from server
                            assignedGoalId = plaidTx.assigned_goal_id,
                            assignedGoalName = plaidTx.assigned_goal_name,
                            assignedGoalIcon = plaidTx.assigned_goal_icon,
                            assignedGoals = plaidTx.assigned_goals,
                            // Category verification fields from server
                            isVerified = plaidTx.is_verified,
                            verifiedAt = plaidTx.verified_at,
                            userNotes = plaidTx.user_notes,
                            isExcluded = plaidTx.is_excluded,
                            isHidden = plaidTx.is_hidden,
                            isSubscription = plaidTx.is_subscription,
                            contextIds = plaidTx.context_ids
                        )
                    } else {
                        // Fallback to client-side AI categorization
                        console.log("⚠️ DEBUG: Falling back to client AI for '${plaidTx.name}' - missing server data!")
                        val aiResult = categorizeTransactionWithAI(
                            merchantName = plaidTx.name,
                            amount = kotlin.math.abs(amount),
                            description = ""
                        )
                        // Respect server's category_source for split/user overrides
                        val fallbackSrc = plaidTx.category_source
                        val effectiveCategorySource = if (fallbackSrc == "split" || fallbackSrc == "user") fallbackSrc else aiResult.source
                        Transaction(
                            id = plaidTx.transaction_id,
                            merchant = plaidTx.name ?: plaidTx.merchant_name ?: StringKey.ACTIVITY_UNKNOWN.tr(),
                            amount = amount,
                            date = localDate,
                            category = aiResult.category,
                            account = accountName,
                            isDebit = isDebit,
                            aiConfidence = (aiResult.confidence * 100).toInt(),
                            categorySource = effectiveCategorySource,
                            categoryLevel1 = aiResult.categoryLevel1,
                            categoryLevel2 = aiResult.categoryLevel2,
                            categoryLevel3 = aiResult.categoryLevel3,
                            confidenceLevel = "UNKNOWN", // Client AI fallback
                            // Split/Distribute fields from server
                            parentTransactionId = plaidTx.parent_transaction_id,
                            childType = plaidTx.child_type,
                            isSplitParent = plaidTx.is_split_parent,
                            isDistributedParent = plaidTx.is_distributed_parent,
                            splitOrder = plaidTx.split_order,
                            distributionStartMonth = plaidTx.distribution_start_month,
                            distributionEndMonth = plaidTx.distribution_end_month,
                            distributionMonthIndex = plaidTx.distribution_month_index,
                            distributionTotalMonths = plaidTx.distribution_total_months,
                            // Goal assignment fields from server
                            assignedGoalId = plaidTx.assigned_goal_id,
                            assignedGoalName = plaidTx.assigned_goal_name,
                            assignedGoalIcon = plaidTx.assigned_goal_icon,
                            assignedGoals = plaidTx.assigned_goals,
                            // Category verification fields from server
                            isVerified = plaidTx.is_verified,
                            verifiedAt = plaidTx.verified_at,
                            userNotes = plaidTx.user_notes,
                            isExcluded = plaidTx.is_excluded,
                            isHidden = plaidTx.is_hidden,
                            isSubscription = plaidTx.is_subscription,
                            contextIds = plaidTx.context_ids
                        )
                    }
                } else {
                    // Income transactions - use server category if available
                    val serverCategory = plaidTx.personal_finance_category
                    Transaction(
                        id = plaidTx.transaction_id,
                        merchant = plaidTx.name ?: plaidTx.merchant_name ?: StringKey.ACTIVITY_UNKNOWN.tr(),
                        amount = amount,
                        date = localDate,
                        category = serverCategory?.let {
                            utils.CategoryUtils.formatTwoLevelCategoryLocalized(it.primary, it.detailed)
                        } ?: plaidTx.category?.firstOrNull() ?: StringKey.CATEGORY_INCOME.tr(),
                        account = accountName,
                        isDebit = isDebit,
                        aiConfidence = null,
                        categorySource = plaidTx.category_source ?: "plaid",
                        categoryLevel1 = serverCategory?.primary ?: "",
                        categoryLevel2 = serverCategory?.detailed ?: "",
                        categoryLevel3 = "",
                        confidenceLevel = serverCategory?.confidence_level, // Use server's confidence level
                        // Split/Distribute fields from server
                        parentTransactionId = plaidTx.parent_transaction_id,
                        childType = plaidTx.child_type,
                        isSplitParent = plaidTx.is_split_parent,
                        isDistributedParent = plaidTx.is_distributed_parent,
                        splitOrder = plaidTx.split_order,
                        distributionStartMonth = plaidTx.distribution_start_month,
                        distributionEndMonth = plaidTx.distribution_end_month,
                        // Goal assignment fields from server
                        assignedGoalId = plaidTx.assigned_goal_id,
                        assignedGoalName = plaidTx.assigned_goal_name,
                        assignedGoalIcon = plaidTx.assigned_goal_icon,
                        assignedGoals = plaidTx.assigned_goals,
                        // Category verification fields from server
                        isVerified = plaidTx.is_verified,
                        verifiedAt = plaidTx.verified_at,
                        userNotes = plaidTx.user_notes,
                        isExcluded = plaidTx.is_excluded,
                        isHidden = plaidTx.is_hidden,
                        isSubscription = plaidTx.is_subscription,
                        contextIds = plaidTx.context_ids
                    )
                }
            }.toMutableList()
    }

    /**
     * Build a flat sorted list of transactions:
     * - Split children appear immediately after their parent (ordered by splitOrder)
     * - Distributed children appear at their own date position
     * - Category filter is applied with special handling for split parents
     */
    private fun buildFlatSortedList(
        allTransactions: List<Transaction>,
        categoryFilter: String?,
        subCategoryFilter: String? = null,
    ): List<Transaction> {
        // Hide parents — only show normal transactions and children
        val visible = allTransactions.filter { tx ->
            !tx.isSplitParent && !tx.isDistributedParent
        }

        // Apply category filter. Sub-category narrows further; if no
        // sub-cat is selected, all sub-cats of the chosen primary pass.
        val filtered = if (categoryFilter != null) {
            visible.filter { tx ->
                val primaryMatches = tx.categoryLevel1.equals(categoryFilter, ignoreCase = true)
                if (!primaryMatches) return@filter false
                if (subCategoryFilter == null) true
                else tx.categoryLevel2.equals(subCategoryFilter, ignoreCase = true)
            }
        } else visible

        // Sort: split children go to their parent's date, distributed children at their own date
        // Group split children by parent so they stay together
        val splitChildren = filtered.filter { it.isSplitChild }
        val nonSplitChildren = filtered.filter { !it.isSplitChild }

        // Sort non-split by date descending
        val sorted = nonSplitChildren.sortedByDescending { it.date.getTime() }.toMutableList()

        // Insert split children at their parent's date position, grouped by parent
        val splitByParent = splitChildren.groupBy { it.parentTransactionId }
        for ((parentId, kids) in splitByParent) {
            val parent = allTransactions.find { it.id == parentId }
            val parentDate = parent?.date?.getTime() ?: 0.0
            val insertIdx = sorted.indexOfFirst { it.date.getTime() <= parentDate }
            val sortedKids = kids.sortedBy { it.splitOrder ?: 0 }
            if (insertIdx >= 0) {
                sorted.addAll(insertIdx, sortedKids)
            } else {
                sorted.addAll(sortedKids)
            }
        }

        return sorted
    }

    /**
     * Verify a transaction's category (confirm AI/Plaid category).
     * Calls POST /api/transactions/{id}/verify endpoint.
     */
    private fun verifyTransactionCategory(transactionId: String) {
        MainScope().launch {
            try {
                val response = apiService.verifyTransactionCategory(transactionId)
                console.log("Verified: ${response.message}, propagated to ${response.propagated_count} similar")
                // Update the verified transaction in local array
                val verifiedTx = transactions.firstOrNull { it.id == transactionId }
                if (verifiedTx != null) {
                    val idx = transactions.indexOf(verifiedTx)
                    transactions[idx] = verifiedTx.copy(isVerified = true)
                    // Also update other identical transactions (same merchant + category) to match server propagation
                    if (response.propagated_count > 0) {
                        for (i in transactions.indices) {
                            val tx = transactions[i]
                            if (tx.id != transactionId && !tx.isVerified &&
                                tx.merchant == verifiedTx.merchant && tx.category == verifiedTx.category
                            ) {
                                transactions[i] = tx.copy(isVerified = true)
                                // Update DOM badge for this propagated transaction
                                val badgeEl = kotlinx.browser.document.getElementById("verify-badge-${tx.id}")
                                if (badgeEl != null) {
                                    val info = utils.CategoryUtils.getConfidenceInfo(
                                        categorySource = tx.categorySource,
                                        confidenceLevel = tx.confidenceLevel,
                                        isVerified = true
                                    )
                                    val color = when (info.colorCode) {
                                        "dark_green" -> "#2E7D32"
                                        "pale_green" -> "#66BB6A"
                                        "dark_orange" -> "#EF6C00"
                                        "pale_red" -> "#E57373"
                                        "dark_red" -> "#C62828"
                                        "blue" -> "#1976D2"
                                        else -> "#757575"
                                    }
                                    val propLabelHtml = if (info.label.isNotEmpty()) {
                                        "<span style=\"font-size:10px;font-weight:500;color:$color;background:${color}22;padding:2px 4px;border-radius:4px;white-space:nowrap;\">${info.label}</span>"
                                    } else ""
                                    badgeEl.innerHTML = "<span style=\"font-size:12px;color:#4CAF50;font-weight:bold;\">\u2713</span>$propLabelHtml"
                                }
                            }
                        }
                    }
                }
            } catch (e: Exception) {
                console.error("Failed to verify category:", e)
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    suspend fun refreshTransactions(button: HTMLElement?) {
        val t = WebTheme.current
        if (isRefreshing) return // Prevent multiple simultaneous refreshes

        isRefreshing = true
        val previousCount = transactions.size

        // Update button to show loading state (keep discrete style)
        button?.let {
            it.textContent = StringKey.ACTIVITY_SYNCING.tr()
            it.asDynamic().disabled = true
            it.style.opacity = "0.6"
            it.style.cursor = "not-allowed"
        }

        // Update status line to show fetching state
        updateStatusLine(StringKey.ACTIVITY_FETCHING.tr(bankName))

        try {
            console.log("Refreshing transactions...")
            apiService.refreshTransactions()
            loadTransactions(forceRefresh = true)

            val newCount = transactions.size
            val diff = newCount - previousCount

            // If relink needed, show relink button instead of success
            if (needsRelink) {
                button?.let {
                    it.textContent = StringKey.ACCOUNT_RELINK_BUTTON.tr()
                    it.style.color = t.targetOrange
                    it.style.borderColor = t.targetOrange
                    it.style.opacity = "1"
                    it.asDynamic().disabled = false
                    it.style.cursor = "pointer"
                }
                isRefreshing = false
                return
            }

            // Show success feedback briefly with green text (keep discrete style)
            val successMessage = when {
                diff > 0 -> "✓ +$diff new"
                diff < 0 -> "✓ $diff"
                else -> "✓ Up to date"
            }

            button?.let {
                it.textContent = successMessage
                it.style.color = t.incomeGreen
                it.style.borderColor = t.incomeGreen
                it.style.opacity = "1"
            }

            // Reset to original style after brief feedback
            kotlinx.browser.window.setTimeout({
                button?.let {
                    it.textContent = StringKey.ACTIVITY_SYNC.tr()
                    it.style.background = "transparent"
                    it.style.color = t.textSecondary
                    it.style.borderColor = t.border
                    it.asDynamic().disabled = false
                    it.style.cursor = "pointer"
                }
                isRefreshing = false
            }, 1500)
        } catch (e: Exception) {
            console.error("Failed to refresh transactions:", e)

            // Handle auth errors by triggering re-auth
            if (e.message == "AUTH_REQUIRED") {
                console.log("Authentication required - triggering re-auth")
                button?.let {
                    it.textContent = StringKey.ACTIVITY_SYNC.tr()
                    it.style.background = "transparent"
                    it.style.color = t.textSecondary
                    it.style.borderColor = t.border
                    it.asDynamic().disabled = false
                    it.style.cursor = "pointer"
                }
                isRefreshing = false
                onAuthError()
                return
            }

            // Show error feedback with red text (keep discrete style)
            // Parse error message for better feedback
            val errorText = when {
                e.message?.contains("500") == true -> "✗ Server error"
                e.message?.contains("404") == true -> "✗ Not linked"
                e.message?.contains("network") == true -> "✗ Offline"
                else -> "✗ Error"
            }
            button?.let {
                it.textContent = errorText
                it.style.color = t.expenseRed
                it.style.borderColor = t.expenseRed
                it.style.opacity = "1"
            }

            // Reset status line to previous state
            updateStatusLine(getStatusText())

            // Reset to original style after brief feedback
            kotlinx.browser.window.setTimeout({
                button?.let {
                    it.textContent = StringKey.ACTIVITY_SYNC.tr()
                    it.style.background = "transparent"
                    it.style.color = t.textSecondary
                    it.style.borderColor = t.border
                    it.asDynamic().disabled = false
                    it.style.cursor = "pointer"
                }
                isRefreshing = false
            }, 2000)
        }
    }

    // MARK: - Multi-Select Batch Actions

    private fun clearSelection() {
        val t = WebTheme.current
        val ids = selectedTransactionIds.toList()
        selectedTransactionIds.clear()
        showClearConfirmation = false
        // Reset icon styles — revert background, border, and glow
        ids.forEach { txId ->
            val iconEl = document.getElementById("txn-icon-$txId") as? HTMLElement
            if (iconEl != null) {
                val catColor = iconEl.getAttribute("data-cat-color") ?: "#666"
                iconEl.style.backgroundColor = theme.getIconCircleFill(catColor, t.isDark)
                iconEl.style.borderColor = catColor
                iconEl.style.boxShadow = "none"
            }
        }
        renderBatchActionMenu()
    }

    private fun renderBatchActionMenu() {
        val t = WebTheme.current
        val container = document.getElementById("batch-action-menu") as? HTMLElement ?: return
        container.innerHTML = ""

        if (selectedTransactionIds.isEmpty()) return

        val menuEl = document.createElement("div") as HTMLElement
        menuEl.style.cssText = "background: ${t.card}; border-radius: 12px; padding: 16px; margin-top: 12px; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};"

        if (showClearConfirmation) {
            // Clear confirmation prompt
            menuEl.innerHTML = """
                <div style="color: #fff; font-size: 14px; margin-bottom: 12px;">
                    ${StringKey.MULTISELECT_CLEAR_PROMPT.tr().replace("%s", selectedTransactionIds.size.toString())}
                </div>
                <div style="display: flex; gap: 8px;">
                    <span id="batch-clear-yes" style="padding: 8px 16px; cursor: pointer; color: #ff4757; background: #2C2C2E; border-radius: 8px; font-weight: 600;">${StringKey.MULTISELECT_CLEAR.tr()}</span>
                    <span id="batch-clear-no" style="padding: 8px 16px; cursor: pointer; color: #00d9ff; background: #2C2C2E; border-radius: 8px; font-weight: 600;">${StringKey.MULTISELECT_KEEP.tr()}</span>
                </div>
            """.trimIndent()
            container.appendChild(menuEl)
            (document.getElementById("batch-clear-yes") as? HTMLElement)?.addEventListener("click", { clearSelection() })
            (document.getElementById("batch-clear-no") as? HTMLElement)?.addEventListener("click", {
                showClearConfirmation = false
                renderBatchActionMenu()
            })
            return
        }

        // Count excluded vs non-excluded in selection
        val excludedIds = selectedTransactionIds.filter { txId -> transactions.any { it.id == txId && it.isExcluded } }
        val nonExcludedIds = selectedTransactionIds.filter { txId -> transactions.any { it.id == txId && !it.isExcluded } }

        val excludeButton = if (nonExcludedIds.isNotEmpty()) {
            """<div id="batch-exclude" style="padding:10px; cursor:pointer; color:#FF9800; border-radius:8px; margin-bottom:4px; display:flex; align-items:center; gap:8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF9800" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"/><path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.354 9.354 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"/><path d="M3 3l18 18"/></svg>
                ${StringKey.MULTISELECT_EXCLUDE.tr()} (${nonExcludedIds.size})
            </div>"""
        } else ""

        val includeButton = if (excludedIds.isNotEmpty()) {
            """<div id="batch-include" style="padding:10px; cursor:pointer; color:#4CAF50; border-radius:8px; margin-bottom:4px; display:flex; align-items:center; gap:8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"/><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"/></svg>
                Include (${excludedIds.size})
            </div>"""
        } else ""

        menuEl.innerHTML = """
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
                <span style="color:#fff; font-weight:bold;">${StringKey.MULTISELECT_COUNT.tr().replace("%s", selectedTransactionIds.size.toString())}</span>
                <span id="batch-close" style="cursor:pointer; color:#666; font-size: 18px;">✕</span>
            </div>
            <div id="batch-change-category" style="padding:10px; cursor:pointer; color:#00d9ff; border-radius:8px; margin-bottom:4px; display:flex; align-items:center; gap:8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00d9ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.859 6h-2.834a2.025 2.025 0 0 0 -2.025 2.025v8.95a2.025 2.025 0 0 0 2.025 2.025h8.95a2.025 2.025 0 0 0 2.025 -2.025v-2.834"/><path d="M15.586 3.586a2 2 0 0 1 2.828 2.828l-6.414 6.586h-3v-3z"/></svg>
                ${StringKey.MULTISELECT_CHANGE_CATEGORY.tr()}
            </div>
            <div id="batch-assign-goal" style="padding:10px; cursor:pointer; color:#4CAF50; border-radius:8px; margin-bottom:4px; display:flex; align-items:center; gap:8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                ${StringKey.MULTISELECT_ASSIGN_TO_GOAL.tr()}
            </div>
            <div id="batch-assign-context" style="padding:10px; cursor:pointer; color:#8B5CF6; border-radius:8px; margin-bottom:4px; display:flex; align-items:center; gap:8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                ${StringKey.CONTEXT_BATCH_ASSIGN.tr()}
            </div>
            $excludeButton
            $includeButton
        """.trimIndent()
        container.appendChild(menuEl)

        // Add hover effects
        listOf("batch-change-category", "batch-assign-goal", "batch-assign-context", "batch-exclude", "batch-include").forEach { btnId ->
            (document.getElementById(btnId) as? HTMLElement)?.apply {
                addEventListener("mouseover", { style.backgroundColor = "#2C2C2E" })
                addEventListener("mouseout", { style.backgroundColor = "transparent" })
            }
        }

        // Close button -> show clear confirmation
        (document.getElementById("batch-close") as? HTMLElement)?.addEventListener("click", {
            showClearConfirmation = true
            renderBatchActionMenu()
        })

        // Change Category
        (document.getElementById("batch-change-category") as? HTMLElement)?.addEventListener("click", {
            showBatchCategoryPicker()
        })

        // Assign to Goal
        (document.getElementById("batch-assign-goal") as? HTMLElement)?.addEventListener("click", {
            showBatchGoalPicker()
        })

        // Assign Context
        (document.getElementById("batch-assign-context") as? HTMLElement)?.addEventListener("click", {
            showBatchContextPicker()
        })

        // Exclude (only non-excluded items)
        (document.getElementById("batch-exclude") as? HTMLElement)?.addEventListener("click", {
            showBatchExcludeConfirm(nonExcludedIds.toList())
        })

        // Include (only excluded items)
        (document.getElementById("batch-include") as? HTMLElement)?.addEventListener("click", {
            showBatchInclude(excludedIds.toList())
        })
    }


    private fun showBatchCategoryPicker() {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000;
            display: flex; align-items: center; justify-content: center;
        """

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 400px; width: 90%;
            max-height: 70vh; overflow-y: auto; color: ${t.textPrimary};
        """
        overlay.appendChild(modal)
        document.body?.appendChild(overlay)
        overlay.addEventListener("click", { e -> if (e.target == overlay) overlay.remove() })

        val primaries = ml.getSelectableCategories(true).filter { it.plaidCode != "__DIVIDER__" }

        // Apply takes either a bare primary code ("TRANSPORTATION") or the
        // server's "PRIMARY/DETAILED" format. updateTransactionCategory parses
        // the slash to set both columns; passing only a primary makes both
        // columns equal, which is the existing "primary-only" behavior.
        fun apply(serverCode: String, displayName: String) {
            overlay.remove()
            val ids = selectedTransactionIds.toList()
            MainScope().launch {
                try {
                    val result = apiService.batchUpdateCategory(ids, serverCode)
                    if (result.failedCount > 0) {
                        console.log("Batch category partial failure: ${result.failedCount} failed")
                    }
                    ids.forEach { txId ->
                        val catEl = document.getElementById("txn-category-$txId") as? HTMLElement
                        if (catEl != null) {
                            catEl.style.color = "#666"
                            catEl.style.fontWeight = "normal"
                            catEl.textContent = displayName
                        }
                    }
                    clearSelection()
                } catch (e: Exception) {
                    console.error("Batch category update failed: ${e.message}")
                    if (e.message == "AUTH_REQUIRED") onAuthError()
                }
            }
        }

        fun rowEl(label: String, color: String?, onClick: () -> Unit, trailing: String? = null): HTMLElement {
            val row = document.createElement("div") as HTMLElement
            row.style.cssText = "padding: 10px 12px; cursor: pointer; border-radius: 8px; margin-bottom: 2px; display: flex; align-items: center; gap: 8px;"
            if (color != null) {
                val dot = document.createElement("span") as HTMLElement
                dot.style.cssText = "width: 12px; height: 12px; border-radius: 50%; background: $color; flex-shrink: 0;"
                row.appendChild(dot)
            }
            val name = document.createElement("span") as HTMLElement
            name.textContent = label
            name.style.cssText = "flex: 1;"
            row.appendChild(name)
            if (trailing != null) {
                val tr = document.createElement("span") as HTMLElement
                tr.textContent = trailing
                tr.style.cssText = "color: ${t.textSecondary}; font-size: 14px;"
                row.appendChild(tr)
            }
            row.addEventListener("mouseover", { row.style.backgroundColor = "#2C2C2E" })
            row.addEventListener("mouseout", { row.style.backgroundColor = "transparent" })
            row.addEventListener("click", { onClick() })
            return row
        }

        // Mutually recursive views (top-level ↔ drilled subcategories) — declared
        // as lateinit lambdas so each can reference the other regardless of order.
        lateinit var renderTopLevel: () -> Unit
        lateinit var renderSubcategories: (ml.SelectableCategory) -> Unit

        renderTopLevel = {
            modal.innerHTML = ""
            val title = document.createElement("h3") as HTMLElement
            title.textContent = StringKey.MULTISELECT_CHANGE_CATEGORY.tr()
            title.style.cssText = "margin: 0 0 16px; color: #fff;"
            modal.appendChild(title)
            primaries.forEach { primary ->
                val color = utils.CategoryUtils.getCategoryColor(primary.name, userCategoryColors)
                modal.appendChild(rowEl(primary.name, color, { renderSubcategories(primary) }, trailing = "›"))
            }
        }

        renderSubcategories = { primary ->
            modal.innerHTML = ""
            val header = document.createElement("div") as HTMLElement
            header.style.cssText = "display: flex; align-items: center; gap: 8px; margin-bottom: 12px;"
            val back = document.createElement("button") as HTMLElement
            back.textContent = "←"
            back.style.cssText = "background: transparent; border: none; color: ${t.textSecondary}; font-size: 18px; cursor: pointer; padding: 4px 8px;"
            back.addEventListener("click", { renderTopLevel() })
            header.appendChild(back)
            val title = document.createElement("h3") as HTMLElement
            title.textContent = primary.name
            title.style.cssText = "margin: 0; color: #fff; flex: 1;"
            header.appendChild(title)
            modal.appendChild(header)

            // Allow applying the primary on its own without drilling into a subcategory.
            val color = utils.CategoryUtils.getCategoryColor(primary.name, userCategoryColors)
            modal.appendChild(rowEl("${primary.name} (general)", color, { apply(primary.plaidCode, primary.name) }))

            primary.subcategories.forEach { sub ->
                val displayName = "${primary.name} → ${sub.name}"
                modal.appendChild(rowEl(sub.name, null, { apply("${primary.plaidCode}/${sub.plaidCode}", displayName) }))
            }
        }

        renderTopLevel()
    }

    private fun showBatchGoalPicker() {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000;
            display: flex; align-items: center; justify-content: center;
        """

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 400px; width: 90%;
            max-height: 70vh; overflow-y: auto; color: ${t.textPrimary};
        """
        modal.innerHTML = "<h3 style='margin: 0 0 16px; color: #fff;'>${StringKey.MULTISELECT_ASSIGN_TO_GOAL.tr()}</h3><div id='batch-goal-list' style='color: #888;'>Loading...</div>"
        overlay.appendChild(modal)
        document.body?.appendChild(overlay)

        overlay.addEventListener("click", { e ->
            if (e.target == overlay) overlay.remove()
        })

        // Load goals
        val goalsRepository = repository.createSavingsGoalsRepositoryWithAuth(
            getAuthToken = { ApiService.getSharedAuthToken() ?: "" }
        )
        MainScope().launch {
            try {
                val userId = auth.UserIdManager().getUserId()
                val result = goalsRepository.getActiveGoals(userId)
                val goalList = document.getElementById("batch-goal-list") as? HTMLElement ?: return@launch
                if (result is repository.DatabaseResult.Success) {
                    val goals = result.data
                    if (goals.isEmpty()) {
                        goalList.innerHTML = "<p style='color: #888;'>No goals found</p>"
                        return@launch
                    }
                    // "None" option to remove goal assignments
                    var goalsHtml = """<div class="batch-goal-option" data-goal-id="__none__" style="padding: 12px; cursor: pointer; border-radius: 8px; margin-bottom: 4px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #333;">
                            <span style="font-size: 20px;">✕</span>
                            <span style="color: #FF9800; font-weight: 500;">None (remove goal)</span>
                        </div>"""
                    goals.forEach { goal ->
                        val iconHtml = CategoryIconSvg.renderGoalIcon(goal.iconEmoji, size = 20, color = "#66A3FF")
                        goalsHtml += """<div class="batch-goal-option" data-goal-id="${goal.id}" style="padding: 12px; cursor: pointer; border-radius: 8px; margin-bottom: 4px; display: flex; align-items: center; gap: 10px;">
                            $iconHtml
                            <span style="color: #66A3FF; font-weight: 500;">${goal.name}</span>
                        </div>"""
                    }
                    goalList.innerHTML = goalsHtml
                    val goalOptions = goalList.querySelectorAll(".batch-goal-option")
                    for (i in 0 until goalOptions.length) {
                        (goalOptions.item(i) as? HTMLElement)?.apply {
                            addEventListener("mouseover", { style.backgroundColor = "#2C2C2E" })
                            addEventListener("mouseout", { style.backgroundColor = "transparent" })
                            addEventListener("click", {
                                val goalId = getAttribute("data-goal-id") ?: return@addEventListener
                                overlay.remove()
                                val ids = selectedTransactionIds.toList()
                                MainScope().launch {
                                    try {
                                        if (goalId == "__none__") {
                                            // Remove goal assignments
                                            val batchResult = apiService.batchRemoveGoal(ids)
                                            if (batchResult.failedCount > 0) {
                                                console.log("Batch remove goal partial failure: ${batchResult.failedCount} failed")
                                            }
                                            // Spot-update: restore category text
                                            ids.forEach { txId ->
                                                val idx = transactions.indexOfFirst { it.id == txId }
                                                if (idx >= 0) transactions[idx] = transactions[idx].copy(assignedGoalId = null, assignedGoalName = null, assignedGoalIcon = null)
                                                val catEl = document.getElementById("txn-category-$txId") as? HTMLElement
                                                val tx = if (idx >= 0) transactions[idx] else null
                                                if (catEl != null && tx != null) {
                                                    catEl.style.color = "#666"
                                                    catEl.style.fontWeight = "normal"
                                                    catEl.textContent = if (tx.categoryLevel1.isNotEmpty() && tx.categoryLevel2.isNotEmpty()) {
                                                        utils.CategoryUtils.formatTwoLevelCategoryLocalized(tx.categoryLevel1, tx.categoryLevel2)
                                                    } else {
                                                        tx.category
                                                    }
                                                }
                                            }
                                        } else {
                                            // Assign to goal
                                            val goalName = querySelector("span:last-child")?.textContent ?: "Goal"
                                            val batchResult = apiService.batchAssignGoal(ids, goalId)
                                            if (batchResult.failedCount > 0) {
                                                console.log("Batch goal assign partial failure: ${batchResult.failedCount} failed")
                                            }
                                            // Spot-update goal display in DOM
                                            ids.forEach { txId ->
                                                val idx = transactions.indexOfFirst { it.id == txId }
                                                if (idx >= 0) transactions[idx] = transactions[idx].copy(assignedGoalId = goalId, assignedGoalName = goalName)
                                                val catEl = document.getElementById("txn-category-$txId") as? HTMLElement
                                                if (catEl != null) {
                                                    catEl.style.color = "#66A3FF"
                                                    catEl.style.fontWeight = "500"
                                                    catEl.textContent = "\uD83C\uDFAF $goalName"
                                                }
                                            }
                                        }
                                        clearSelection()
                                    } catch (e: Exception) {
                                        console.error("Batch goal operation failed: ${e.message}")
                                        if (e.message == "AUTH_REQUIRED") onAuthError()
                                    }
                                }
                            })
                        }
                    }
                } else {
                    goalList.innerHTML = "<p style='color: #ff4757;'>Failed to load goals</p>"
                }
            } catch (e: Exception) {
                console.error("Failed to load goals: ${e.message}")
            }
        }
    }

    private fun showBatchContextPicker() {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000;
            display: flex; align-items: center; justify-content: center;
        """

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 400px; width: 90%;
            max-height: 70vh; overflow-y: auto; color: ${t.textPrimary};
        """
        modal.innerHTML = "<h3 style='margin: 0 0 16px; color: #fff;'>${StringKey.CONTEXT_BATCH_ASSIGN.tr()}</h3><div id='batch-context-list' style='color: #888;'>Loading...</div>"
        overlay.appendChild(modal)
        document.body?.appendChild(overlay)

        overlay.addEventListener("click", { e ->
            if (e.target == overlay) overlay.remove()
        })

        // Load contexts
        MainScope().launch {
            try {
                val body = apiService.getContexts()
                val contexts = body.contexts
                val contextList = document.getElementById("batch-context-list") as? HTMLElement ?: return@launch
                if (contexts.isEmpty()) {
                    contextList.innerHTML = "<p style='color: #888;'>No contexts found</p>"
                    return@launch
                }
                val selectedContextIds = mutableSetOf<String>()
                var html = ""
                contexts.forEach { ctx ->
                    html += """<div class="batch-ctx-option" data-ctx-id="${ctx.id}" style="display: flex; align-items: center; gap: 12px; padding: 10px 12px; cursor: pointer; border-radius: 8px; margin-bottom: 2px;">
                        <input type="checkbox" class="batch-ctx-checkbox" data-ctx-id="${ctx.id}" style="accent-color: ${ctx.color}; width: 18px; height: 18px; flex-shrink: 0; cursor: pointer;" />
                        <span style="width: 8px; height: 8px; border-radius: 50%; background: ${ctx.color}; flex-shrink: 0;"></span>
                        <span style="color: #fff; font-size: 15px;">${ctx.name}</span>
                    </div>"""
                }
                html += """<button id="batch-ctx-done" style="width: 100%; margin-top: 16px; padding: 12px; background: #00D9FF; color: #000; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer;">Done</button>"""
                contextList.innerHTML = html

                // Wire checkbox toggle on row click
                val ctxOptions = contextList.querySelectorAll(".batch-ctx-option")
                for (i in 0 until ctxOptions.length) {
                    (ctxOptions.item(i) as? HTMLElement)?.apply {
                        addEventListener("mouseover", { style.backgroundColor = "#2C2C2E" })
                        addEventListener("mouseout", { style.backgroundColor = "transparent" })
                        addEventListener("click", { e ->
                            val ctxId = getAttribute("data-ctx-id") ?: return@addEventListener
                            val cb = querySelector(".batch-ctx-checkbox") as? org.w3c.dom.HTMLInputElement ?: return@addEventListener
                            // Don't double-toggle if the checkbox itself was clicked
                            if ((e.target as? org.w3c.dom.Element)?.tagName?.uppercase() != "INPUT") {
                                cb.checked = !cb.checked
                            }
                            if (cb.checked) selectedContextIds.add(ctxId) else selectedContextIds.remove(ctxId)
                        })
                    }
                }

                // Wire checkboxes directly too
                val checkboxes = contextList.querySelectorAll(".batch-ctx-checkbox")
                for (i in 0 until checkboxes.length) {
                    (checkboxes.item(i) as? org.w3c.dom.HTMLInputElement)?.apply {
                        addEventListener("click", { e ->
                            e.stopPropagation()
                            val ctxId = getAttribute("data-ctx-id") ?: return@addEventListener
                            if (checked) selectedContextIds.add(ctxId) else selectedContextIds.remove(ctxId)
                        })
                    }
                }

                // Done button
                (document.getElementById("batch-ctx-done") as? HTMLElement)?.addEventListener("click", {
                    if (selectedContextIds.isEmpty()) {
                        overlay.remove()
                        return@addEventListener
                    }
                    val ids = selectedTransactionIds.toList()
                    overlay.remove()
                    MainScope().launch {
                        try {
                            val result = apiService.batchAssignContexts(ids, selectedContextIds.toList())
                            console.log("Batch context assign: ${result.updated} updated")
                            // Optimistic local record so the assignment survives in
                            // the demo (loadTransactions no-ops there).
                            ids.forEach { txId ->
                                val idx = transactions.indexOfFirst { it.id == txId }
                                if (idx >= 0) transactions[idx] = transactions[idx].copy(contextIds = selectedContextIds.toList())
                            }
                            clearSelection()
                            // Reload from server (not force_refresh which re-syncs Plaid)
                            loadTransactions(forceRefresh = false)
                        } catch (e: Exception) {
                            console.error("Batch context assign failed: ${e.message}")
                            if (e.message == "AUTH_REQUIRED") onAuthError()
                        }
                    }
                })
            } catch (e: Exception) {
                console.error("Failed to load contexts: ${e.message}")
                val contextList = document.getElementById("batch-context-list") as? HTMLElement
                contextList?.innerHTML = "<p style='color: #ff4757;'>Failed to load contexts</p>"
            }
        }
    }

    private val excludedBadgeHtml = """<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 6px;background-color:rgba(255,152,0,0.15);border-radius:4px;font-size:10px;font-weight:500;color:#FF9800;white-space:nowrap;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>${StringKey.TRANSACTION_EXCLUDED_SHORT.tr()}</span>"""

    private fun showBatchExcludeConfirm(ids: List<String>) {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000;
            display: flex; align-items: center; justify-content: center;
        """

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; color: ${t.textPrimary};
        """
        modal.innerHTML = """
            <h3 style="margin: 0 0 16px;">${StringKey.MULTISELECT_EXCLUDE_CONFIRM.tr().replace("%s", ids.size.toString())}</h3>
            <div style="display: flex; gap: 12px; justify-content: flex-end;">
                <button id="batch-exclude-cancel" style="padding: 10px 20px; background: #2C2C2E; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;">Cancel</button>
                <button id="batch-exclude-confirm" style="padding: 10px 20px; background: #ff4757; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 600;">${StringKey.MULTISELECT_EXCLUDE.tr()}</button>
            </div>
        """.trimIndent()
        overlay.appendChild(modal)
        document.body?.appendChild(overlay)

        overlay.addEventListener("click", { e ->
            if (e.target == overlay) overlay.remove()
        })

        (document.getElementById("batch-exclude-cancel") as? HTMLElement)?.addEventListener("click", {
            overlay.remove()
        })

        (document.getElementById("batch-exclude-confirm") as? HTMLElement)?.addEventListener("click", {
            overlay.remove()
            MainScope().launch {
                try {
                    val result = apiService.batchExclude(ids)
                    if (result.failedCount > 0) {
                        console.log("Batch exclude partial failure: ${result.failedCount} failed")
                    }
                    // Spot-update: show orange excluded badge (same as existing row rendering)
                    ids.forEach { txId ->
                        // Update local transaction data
                        val idx = transactions.indexOfFirst { it.id == txId }
                        if (idx >= 0) transactions[idx] = transactions[idx].copy(isExcluded = true)
                        // Add excluded badge to merchant line
                        val badgeContainer = document.getElementById("txn-badges-$txId") as? HTMLElement
                        if (badgeContainer != null && !badgeContainer.innerHTML.contains("Excluded")) {
                            badgeContainer.innerHTML += excludedBadgeHtml
                        }
                        // Update category line
                        val catEl = document.getElementById("txn-category-$txId") as? HTMLElement
                        if (catEl != null) {
                            catEl.style.color = "#666"
                            catEl.textContent = StringKey.TRANSACTION_EXCLUDED_SHORT.tr()
                        }
                    }
                    clearSelection()
                } catch (e: Exception) {
                    console.error("Batch exclude failed: ${e.message}")
                    if (e.message == "AUTH_REQUIRED") onAuthError()
                }
            }
        })
    }

    private fun showBatchInclude(ids: List<String>) {
        MainScope().launch {
            try {
                val result = apiService.batchInclude(ids)
                if (result.failedCount > 0) {
                    console.log("Batch include partial failure: ${result.failedCount} failed")
                }
                // Spot-update: remove excluded badge, restore category text
                ids.forEach { txId ->
                    // Update local transaction data
                    val idx = transactions.indexOfFirst { it.id == txId }
                    if (idx >= 0) transactions[idx] = transactions[idx].copy(isExcluded = false)
                    // Remove excluded badge from merchant line
                    val badgeContainer = document.getElementById("txn-badges-$txId") as? HTMLElement
                    if (badgeContainer != null) {
                        // Remove any excluded badge spans
                        val spans = badgeContainer.querySelectorAll("span")
                        for (i in 0 until spans.length) {
                            val span = spans.item(i) as? HTMLElement
                            if (span != null && span.textContent?.contains(StringKey.TRANSACTION_EXCLUDED_SHORT.tr()) == true) {
                                span.remove()
                            }
                        }
                    }
                    // Restore category text
                    val catEl = document.getElementById("txn-category-$txId") as? HTMLElement
                    val tx = if (idx >= 0) transactions[idx] else null
                    if (catEl != null && tx != null) {
                        if (tx.hasGoalAssignment) {
                            catEl.style.color = "#66A3FF"
                            catEl.style.fontWeight = "500"
                            catEl.textContent = "\uD83C\uDFAF ${tx.assignedGoalName ?: "Goal"}"
                        } else {
                            catEl.style.color = "#666"
                            catEl.style.fontWeight = "normal"
                            catEl.textContent = if (tx.categoryLevel1.isNotEmpty() && tx.categoryLevel2.isNotEmpty()) {
                                utils.CategoryUtils.formatTwoLevelCategoryLocalized(tx.categoryLevel1, tx.categoryLevel2)
                            } else {
                                tx.category
                            }
                        }
                    }
                }
                clearSelection()
            } catch (e: Exception) {
                console.error("Batch include failed: ${e.message}")
                if (e.message == "AUTH_REQUIRED") onAuthError()
            }
        }
    }

    // Use shared CategoryUtils for consistent colors across all platforms
    private fun getCategoryColor(category: String): String =
        utils.CategoryUtils.getCategoryColor(category, userCategoryColors)

    private fun getCategoryColorWithAlpha(category: String, alpha: Double): String =
        utils.CategoryUtils.getCategoryColorWithAlpha(category, alpha, userCategoryColors)

    private fun getStatusText(): String {
        if (lastUpdateTimestamp == 0.0) {
            return ""
        }

        val now = Date.now()
        val minutesAgo = ((now - lastUpdateTimestamp) / 60000).toInt()

        val timeAgoText = when {
            minutesAgo < 1 -> StringKey.ACTIVITY_LAST_UPDATED_JUST_NOW.tr(bankName)
            minutesAgo == 1 -> StringKey.ACTIVITY_LAST_UPDATED_ONE_MINUTE.tr(bankName)
            minutesAgo < 60 -> StringKey.ACTIVITY_LAST_UPDATED_MINUTES.tr(bankName, minutesAgo.toString())
            else -> {
                val hoursAgo = minutesAgo / 60
                if (hoursAgo == 1) StringKey.ACTIVITY_LAST_UPDATED_ONE_HOUR.tr(bankName)
                else StringKey.ACTIVITY_LAST_UPDATED_HOURS.tr(bankName, hoursAgo.toString())
            }
        }

        return timeAgoText
    }

    private fun updateStatusLine(text: String) {
        val statusLine = document.getElementById("status-line")
        statusLine?.textContent = text
    }

    suspend fun attachToDOM(elementId: String) {
        // Load transactions first (with forceRefresh if requested by navigation manager)
        loadTransactions(forceRefresh = forceRefresh)

        val renderedElement = render()
        val rootElement = document.getElementById(elementId)
        rootElement?.appendChild(renderedElement)
    }

    private data class BadgeStyle(
        val bg: String,
        val border: String,
        val fg: String,
        val text: String,
    )

    /**
     * True iff [lastRelinkedAt] is non-null and falls within the last [hours].
     * Accepts both ISO-8601 ("2026-05-05T19:00:00Z") and SQLite CURRENT_TIMESTAMP
     * format ("2026-05-05 19:00:00", UTC). Returns false on any parse failure
     * — we'd rather show the standard "stale" badge than misrender the
     * Plaid-stuck variant.
     */
    private fun wasRelinkedWithinHours(lastRelinkedAt: String?, hours: Int): Boolean {
        if (lastRelinkedAt.isNullOrBlank()) return false
        return try {
            val isoString = if (lastRelinkedAt.contains('T')) lastRelinkedAt
                            else lastRelinkedAt.replace(' ', 'T') + "Z"
            val parsed = kotlin.js.Date(isoString)
            val ms = parsed.getTime()
            if (ms.isNaN()) return false
            val ageMs = kotlin.js.Date().getTime() - ms
            ageMs in 0.0..(hours.toLong() * 3600_000.0)
        } catch (_: Throwable) {
            false
        }
    }

    /**
     * Days an account can be quiet before we flag it as stale.
     * Active spending accounts (checking, credit card) transact every few days, so 7 catches
     * silent feed stalls fast. Low-activity accounts (savings, loans, investments) commonly go
     * a month between transactions by design — flagging them weekly would be cry-wolf.
     */
    private fun staleThresholdFor(account: PlaidAccount): Int {
        val type = account.type.lowercase()
        val subtype = (account.subtype ?: "").lowercase()
        val isActiveSpending =
            (type == "depository" && subtype in setOf("checking", "paypal", "prepaid", "cash management")) ||
                (type == "credit" && subtype in setOf("credit card", "paypal"))
        return if (isActiveSpending) 7 else 30
    }

    /**
     * Watches the bottom-of-list sentinel and grows the visible batch when it scrolls in.
     * Re-attaches every render() because the sentinel element is recreated each time.
     *
     * Was previously an IntersectionObserver via raw `js("new IntersectionObserver(callback, opts)")`,
     * but Kotlin/JS suffixes local variable names (e.g. `callback` → `callback_0`)
     * during compilation while the `js("...")` string keeps the name literal —
     * so in the production minified bundle the observer was constructed with an
     * undefined callback and silently never fired. A plain scroll listener has
     * no such interop issue and works identically.
     */
    private fun setupLoadMoreObserver() {
        loadMoreScrollHandler?.let { window.removeEventListener("scroll", it) }
        loadMoreScrollHandler = null
        // No-op if we've already rendered everything.
        if (document.getElementById("txn-list-sentinel") == null) return

        val handler: (org.w3c.dom.events.Event) -> Unit = handler@{ _ ->
            val sentinel = document.getElementById("txn-list-sentinel") ?: run {
                // Sentinel gone — list fully rendered. Detach.
                loadMoreScrollHandler?.let { window.removeEventListener("scroll", it) }
                loadMoreScrollHandler = null
                return@handler
            }
            val rect = sentinel.getBoundingClientRect()
            // Trigger when sentinel comes within 400px of the viewport bottom.
            if (rect.top < window.innerHeight + 400) {
                loadMoreTxns()
            }
        }
        window.addEventListener("scroll", handler)
        loadMoreScrollHandler = handler
        // Fire once after layout settles in case the sentinel is already in view
        // (e.g. very tall viewport, or the user already scrolled before re-render).
        window.setTimeout({ handler(js("new Event('scroll')")) }, 0)
    }

    /**
     * Re-renders the component in place and restores scroll position. Replaces our own
     * root element (found by id) rather than assuming a fixed document container — the
     * list is mounted into different parents depending on the screen, so a hardcoded
     * lookup silently no-ops. Shared by "load more" and any other in-place state toggle
     * (e.g. the "Show pending holds" checkbox) that needs the list to reflect new state
     * without a full page navigation.
     */
    private fun rerenderList() {
        val scrollY = window.scrollY
        val oldRoot = document.getElementById("txn-list-root") ?: return
        val parent = oldRoot.parentElement ?: return
        parent.replaceChild(render(), oldRoot)
        window.scrollTo(0.0, scrollY)
    }

    private fun loadMoreTxns() {
        visibleTxnLimit += txnLoadBatchSize
        // Re-render the component in place. The rows above are identical, so visually the
        // only change is more rows appearing below where the sentinel was.
        rerenderList()
    }

    private fun startRelinkFlow(itemId: String) {
        startPlaidRelinkWithFallback(
            itemId = itemId,
            apiService = apiService,
            onComplete = { resultItemId ->
                // After relink, land the user on the just-relinked item's first
                // account. Falls back to the previously-selected account if the
                // lookup fails (e.g. network blip), but that's the unhappy path —
                // for fresh-link fallback, the previous account_id is gone, so
                // the lookup must succeed for the user to land on fresh data.
                MainScope().launch {
                    val landingAccountId =
                        try {
                            apiService.getLinkedInstitutions()
                                .firstOrNull { it.item_id == resultItemId }
                                ?.accounts?.firstOrNull()?.account_id
                        } catch (e: Exception) {
                            console.warn("Post-relink account lookup failed:", e.message)
                            null
                        } ?: accountId
                    landingAccountId?.let { id ->
                        try {
                            kotlinx.browser.window.sessionStorage.setItem("wiz3r:selectedAccountId", id)
                        } catch (_: Throwable) { /* sessionStorage may be unavailable in private mode */ }
                    }
                    window.location.reload()
                }
            },
        )
    }
}
